import { useState, useEffect, useRef, useMemo, Fragment } from "react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import { OfferBuilderForm } from "./OfferBuilderForm";
import "./FunnelMapper.css";
import "./FunnelBlock.css";
import { Loader2, AlertTriangle, CheckCircle2, ChevronDown, Info, Pencil, Plus, Trash2, Phone, Layers, Mail, X, Archive, ArchiveRestore, ChevronRight, PlayCircle, TrendingUp, DollarSign } from "lucide-react";
import axios from "axios";
import { useRevit } from '../../contexts/RevitContext';
import { useAuth, useUser } from '@clerk/clerk-react';
import { fetchOffers, fetchOffersByDate, openBillingPortal } from '../../utils/apiCalls';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import Icon from '../Icon';
import { getCurrencySymbol } from '../../utils/currencyUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { TrafficSourceLogo } from '../SourceLogo/SourceLogo';

// Import the cache constants
import { CACHE_EXPIRATION_TIME } from '../../contexts/ContentCacheContext';

import lovableLogo from './lovable-color.svg';
import shopifyLogo from './shopify-color.svg';

// Raw source of the tracking scripts, used to inline the code into the Lovable prompt.
/* eslint-disable import/no-webpack-loader-syntax */
import trackerScriptRaw from '!!raw-loader!./trackerScript.js';
import thankYouScriptRaw from '!!raw-loader!./thankYouScript.js';
import emailThankYouScriptRaw from '!!raw-loader!./emailThankYouScript.js';
import callBookingScriptRaw from '!!raw-loader!./callBookingThankYouScript.js';
import funnelStepThankYouScriptRaw from '!!raw-loader!./funnelStepThankYouScript.js';
/* eslint-enable import/no-webpack-loader-syntax */

// Maps a step type to its raw script source and the init function to invoke on load.
const LOVABLE_SCRIPTS = {
  landing:      { raw: trackerScriptRaw,            init: 'initializeTracker' },
  thank_you:    { raw: thankYouScriptRaw,            init: 'initializeThankYou' },
  email:        { raw: emailThankYouScriptRaw,       init: 'initializeEmailThankYou' },
  call_booking: { raw: callBookingScriptRaw,         init: 'initializeCallBooking' },
  custom:       { raw: funnelStepThankYouScriptRaw,  init: 'initializeFunnelStepThankYouScript' },
};

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

// Create a cache object outside the component to persist across renders
const offersCache = {};

const PASTE_LOCATIONS = [
  { 
    platform: 'Select platform...', 
    instructions: 'Choose your website platform to see specific instructions',
    value: ''
  },
  { 
    platform: 'GoHighLevel', 
    instructions: 'Add a "Custom Code" block to your page body',
    value: 'gohighlevel'
  },
  { 
    platform: 'Kajabi', 
    instructions: 'Website → Pages → Customize → Add Content → Custom Code',
    value: 'kajabi'
  },
  { 
    platform: 'Thinkific', 
    instructions: 'Settings → Code & Analytics → Site Footer Code',
    value: 'thinkific'
  },
  { 
    platform: 'WordPress', 
    instructions: 'Add a "Code Block" element to your page and paste the code',
    value: 'wordpress'
  },
  { 
    platform: 'Squarespace', 
    instructions: 'Go to your page editor and add a "Custom code" block to your page.',
    value: 'squarespace'
  },
  { 
    platform: 'Framer', 
    instructions: 'Project/Page Settings → Custom Code → Add to <head>',
    value: 'framer'
  },
  { 
    platform: 'Kit (ConvertKit)', 
    instructions: 'Embed Kit form on a separate landing page (e.g., Squarespace) Note: Built-in Kit landing pages do not work.',
    value: 'kit'
  },
  { 
    platform: 'Webflow', 
    instructions: 'Pages → Settings → Custom Code → Head Code',
    value: 'webflow'
  },
  { 
    platform: 'Other', 
    instructions: 'Find a "code block" element in your page builder and add it anywhere on your page.',
    value: 'other'
  },
];

/* ── Inline step-type chooser ──────────────────────────── */
const InlineStepChooser = ({ slot, isProAccount, hasEmailSignup, hasCallBooking, onSelect, onClose }) => {
  const { setIsProModalOpen } = useRevit();
  const showCallBooking = !slot.isPostThankYou && !hasCallBooking;
  return (
    <div className="fm-step-chooser">
      <div className="fm-step-chooser-head">
        <span className="fm-step-chooser-title">Add a step</span>
        <button className="fm-step-chooser-close" onClick={onClose} type="button"><X size={14} /></button>
      </div>
      <div className="fm-step-chooser-opts">
        <button
          className={`fm-step-chooser-opt${hasEmailSignup ? ' fm-step-chooser-opt--disabled' : ''}`}
          type="button"
          onClick={() => !hasEmailSignup && onSelect('email')}
          disabled={hasEmailSignup}
        >
          <Mail size={15} />
          <div className="fm-step-chooser-opt-text">
            <span className="fm-step-chooser-opt-label">Email Signup</span>
            <span className="fm-step-chooser-opt-desc">Track when visitors opt in to your list</span>
          </div>
          {hasEmailSignup && <span className="fm-chooser-badge">Already added</span>}
        </button>
        {showCallBooking && (
          <button className="fm-step-chooser-opt" type="button" onClick={() => onSelect('call_booking')}>
            <Phone size={15} />
            <div className="fm-step-chooser-opt-text">
              <span className="fm-step-chooser-opt-label">Call Booking</span>
              <span className="fm-step-chooser-opt-desc">Track when prospects book a discovery call</span>
            </div>
          </button>
        )}
        <button
          className={`fm-step-chooser-opt${!isProAccount ? ' fm-step-chooser-opt--disabled' : ''}`}
          type="button"
          onClick={() => {
            if (isProAccount) onSelect('custom');
            else setIsProModalOpen(true);
          }}
        >
          <Layers size={15} />
          <div className="fm-step-chooser-opt-text">
            <span className="fm-step-chooser-opt-label">
              Custom Step
              {!isProAccount && <span className="fm-chooser-badge fm-chooser-badge--pro">PRO</span>}
            </span>
            <span className="fm-step-chooser-opt-desc">Add any custom conversion point</span>
          </div>
        </button>
      </div>
    </div>
  );
};

/* ── Custom step inline form ─────────────────────────────── */
const CustomStepForm = ({ onSubmit, onCancel, isLoading, currency }) => {
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  return (
    <div className="fm-custom-step-form">
      <input
        className="fm-custom-step-name"
        placeholder="Step name (e.g. Upsell Page)…"
        value={name}
        onChange={e => setName(e.target.value)}
        autoFocus
        onKeyDown={e => { if (e.key === 'Escape') onCancel(); }}
      />
      <div className="fm-custom-step-price-row">
        <span className="fm-price-currency">{getCurrencySymbol(currency)}</span>
        <input
          className="fm-price-input"
          type="number"
          min="0"
          placeholder="Value (optional)"
          value={price}
          onChange={e => setPrice(e.target.value)}
        />
      </div>
      <div className="fm-custom-step-actions">
        <button
          className="fm-custom-step-submit"
          type="button"
          onClick={() => name.trim() && onSubmit(name.trim(), price)}
          disabled={!name.trim() || isLoading}
        >
          {isLoading ? <><Loader2 size={12} className="button-icon animate-spin" /> Adding…</> : '+ Add step'}
        </button>
        <button className="fm-custom-step-cancel" type="button" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
};

/* ── Add-step section (button → chooser → form) ─────────── */
const AddStepSection = ({ slot, isActive, activeType, isProAccount, hasEmailSignup, hasCallBooking, isLoading, disabled, onOpen, onSelectType, onSubmitCustom, onClose, currency }) => {
  const { setIsProModalOpen } = useRevit();
  if (!isActive) {
    return (
      <div className="fm-add-step-row">
        <div className="fm-add-step-line" />
        <button 
          className={`fm-inline-add-btn${disabled ? ' fm-inline-add-btn--disabled' : ''}`}
          type="button" 
          onClick={() => {
            if (disabled) setIsProModalOpen(true);
            else onOpen();
          }}
        >
          <Plus size={12} /> Add step
          {disabled && <span className="offer-form-pro-badge">PRO</span>}
        </button>
        <div className="fm-add-step-line" />
      </div>
    );
  }
  if (activeType === 'custom') {
    return (
      <div className="fm-step-chooser-wrap">
        <CustomStepForm onSubmit={onSubmitCustom} onCancel={onClose} isLoading={isLoading} currency={currency} />
      </div>
    );
  }
  return (
    <div className="fm-step-chooser-wrap">
      <InlineStepChooser
        slot={slot}
        isProAccount={isProAccount}
        hasEmailSignup={hasEmailSignup}
        hasCallBooking={hasCallBooking}
        onSelect={onSelectType}
        onClose={onClose}
      />
    </div>
  );
};

const StepCard = ({
  id, title, url, onDelete, onUrlUpdate, onVerified,
  isCustomStep, stageNumber, offer,
  priceIndex, stepId, stepType, description,
  editMode,
  // Price editing props (thank_you steps only)
  currency, onEditPrice, isEditingPrice, newPriceValue, setNewPriceValue,
  editingCustomPrice, setEditingCustomPrice, onSavePrice, onCancelPrice,
  // Pro gating — editing prices is a Pro-only feature. `isProAccount` gates
  // the pencil button in edit mode; `isFounder` always gets access.
  isProAccount,
}) => {
  const { username, setIsProModalOpen } = useRevit();
  const { user } = useUser();
  const { getToken } = useAuth();
  const [copied, setCopied] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [skipped, setSkipped] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [localUrl, setLocalUrl] = useState(url || '');
  const [userHasEdited, setUserHasEdited] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const setupCheckTimer = useRef(null);

  useEffect(() => () => {
    if (setupCheckTimer.current) clearInterval(setupCheckTimer.current);
  }, []);

  useEffect(() => {
    if (!showVideoModal) return;
    if (!document.getElementById('wistia-player-js')) {
      const s1 = document.createElement('script');
      s1.id = 'wistia-player-js';
      s1.src = 'https://fast.wistia.com/player.js';
      s1.async = true;
      document.body.appendChild(s1);
    }
    if (!document.getElementById('wistia-media-js')) {
      const s2 = document.createElement('script');
      s2.id = 'wistia-media-js';
      s2.src = 'https://fast.wistia.com/embed/lnec13qx1p.js';
      s2.async = true;
      s2.type = 'module';
      document.body.appendChild(s2);
    }
  }, [showVideoModal]);

  useEffect(() => { setVerificationResult(null); setSkipped(false); }, [offer?.id]);
  // Reset local state when offer changes or step collapses
  useEffect(() => { setLocalUrl(url || ''); setUserHasEdited(false); }, [url]);
  useEffect(() => { if (!expanded) setUserHasEdited(false); }, [expanded]);

  const isValidUrl = (str) => {
    try {
      const u = new URL(str);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch { return false; }
  };
  const urlIsInvalid = userHasEdited && !!localUrl && !isValidUrl(localUrl);
  const urlIsValid  = !!localUrl && isValidUrl(localUrl);
  const urlIsSaved  = userHasEdited && urlIsValid && localUrl === (url || '');

  // Extract values from offer
  const offerName = offer?.name || '';
  const price = priceIndex === 1 ? offer?.conversion_value : 
                priceIndex === 2 ? offer?.conversion_value2 : 
                offer?.conversion_value3;

  const copyToClipboard = (code, label) => {
    navigator.clipboard.writeText(code);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const handleVerifySetup = () => {
    const pageUrl = urlIsValid ? localUrl : url;
    if (!pageUrl || !isValidUrl(pageUrl)) {
      alert('Please enter your page URL first');
      return;
    }
    const setupUrl = new URL(pageUrl);
    setupUrl.searchParams.set('rtverify', '1');
    if (offerName && offerName.trim()) {
      setupUrl.searchParams.set('utm_offer', offerName.trim());
    }
    if (username) setupUrl.searchParams.set('rtuser', username);
    const stepKey = stepType === 'thank_you'
      ? `thank_you:${priceIndex || 1}`
      : stepType === 'custom'
        ? `custom:${title}`
        : stepType;
    setupUrl.searchParams.set('rtstep', stepKey);
    if (SERVER_URL) setupUrl.searchParams.set('rthost', SERVER_URL);
    const width = 780;
    const height = 480;
    const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);
    // Blank first so we can show a same-origin note, then load their page.
    const popup = window.open(
      '',
      'revtrack-setup-check',
      `popup=yes,width=${width},height=${height},left=${left},top=${top}`
    );
    if (!popup) return;

    setIsVerifying(true);
    setVerificationResult(null);
    let popupBlocked = false;
    let popupPolicyReady = false;
    getToken().then((token) => axios.post(
      `https://${SERVER_URL}/api/check-verification-popup`,
      { url: pageUrl },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
    )).then((response) => {
      popupBlocked = !!response.data?.blocksPopup;
    }).catch(() => {}).finally(() => {
      popupPolicyReady = true;
    });

    const popupIsClosed = () => {
      try {
        return popup.closed;
      } catch {
        return true;
      }
    };

    const waitHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>RevTrack</title></head><body style="margin:0;min-height:100vh;background:#1a1a1a;display:flex;align-items:center;justify-content:center;padding:24px;box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;"><p style="margin:0;max-width:420px;background:#021220;color:#f3f4f6;border:2px solid #00D3F2;border-radius:16px;padding:28px 32px;font-size:20px;font-weight:600;line-height:1.4;box-shadow:0 12px 40px rgba(0,0,0,.45),0 0 0 6px rgba(0,211,242,.18);">Please wait at least 10 seconds before closing the following window.</p></body></html>`;
    let showedWait = false;
    try {
      popup.document.open();
      popup.document.write(waitHtml);
      popup.document.close();
      showedWait = true;
    } catch (error) {
      showedWait = false;
    }

    if (setupCheckTimer.current) clearInterval(setupCheckTimer.current);
    const startPolling = () => {
      if (popupIsClosed()) {
        setIsVerifying(false);
        return;
      }
      const startedAt = new Date(Date.now() - 2000).toISOString();
    let attempts = 0;
    let scriptSeen = false;
    let pageChecks = null;
    if (setupCheckTimer.current) clearInterval(setupCheckTimer.current);

    if (stepType === 'landing') getToken().then((token) => {
      axios.post(`https://${SERVER_URL}/api/verify-landing-page`, {
        url: pageUrl,
        offerName,
        username,
        skipSave: true
      }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      }).then((response) => {
        pageChecks = response.data;
      }).catch(() => {});
    }).catch(() => {});

    setupCheckTimer.current = setInterval(async () => {
      attempts += 1;
      const closed = popupIsClosed();
      if (closed && popupPolicyReady && !popupBlocked) {
        clearInterval(setupCheckTimer.current);
        setIsVerifying(false);
        return;
      }
      try {
        const token = await getToken();
        const response = await axios.get(
          `https://${SERVER_URL}/api/setup-check-ping/${encodeURIComponent(username)}`,
          { headers: { Authorization: `Bearer ${token}` }, params: { since: startedAt, step: stepKey } }
        );
        if (response.data?.success) {
          clearInterval(setupCheckTimer.current);
          setIsVerifying(false);
          try {
            await axios.post(`https://${SERVER_URL}/api/mark-step-verified`, {
              offerName: offer.name,
              stepType,
              stepName: title,
              priceIndex,
              username
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            onVerified?.({ stepType, priceIndex, stepId });
          } catch (verifyError) {
            console.error('Error marking setup check verified:', verifyError);
          }
          setTimeout(() => {
            try {
              if (!popup.closed) popup.close();
            } catch {
              // The page can sever the popup; closing it is optional.
            }
          }, 3000);
          return;
        }
        if (response.data?.seen) scriptSeen = true;
      } catch (error) {
        console.error('Setup check poll failed:', error);
      }
      if (scriptSeen) {
        if (attempts >= 20) {
          clearInterval(setupCheckTimer.current);
          setIsVerifying(false);
        }
        return;
      }
      if (attempts < 8 || !popupPolicyReady) return;
      clearInterval(setupCheckTimer.current);
      setIsVerifying(false);
      if (popupBlocked) {
        setVerificationResult({ valid: false, popupBlocked: true });
        return;
      }
      if (popupIsClosed()) return;
      const failUrl = new URL('/setup-check-missing', window.location.origin);
      const checks = pageChecks?.checks || {};
      const offerStatus = !pageChecks ? 'unknown'
        : !checks.offerMeta ? 'missing'
        : pageChecks.offerMatch === false ? 'mismatch'
        : 'found';
      const usernameStatus = !pageChecks ? 'unknown' : checks.usernameMeta ? 'found' : 'missing';
      const scriptStatus = checks.cloudFrontScript ? 'not-running' : 'not-found';
      failUrl.searchParams.set('offer', offerStatus);
      failUrl.searchParams.set('username', usernameStatus);
      failUrl.searchParams.set('script', scriptStatus);
      popup.location = failUrl.href;
    }, 1000);
    };

    if (!showedWait) {
      popup.location = setupUrl.href;
      startPolling();
      return;
    }

    setTimeout(() => {
      if (popupIsClosed()) {
        setIsVerifying(false);
        return;
      }
      popup.location = setupUrl.href;
      startPolling();
    }, 1200);
  };

  const trackerScript = (name) =>
    `<meta name="username" content="${user ? username : ''}">
<meta name="offer" content="${name.trim()}">
<script src="https://d15dfsr886zcp9.cloudfront.net/tracker_script.js" defer></script>`;

  const thankYouScript = (name, p, isCustomPrice, pIdx) => {
    const usernameTag = `<meta name="username" content="${user ? username : ''}">`;
    const metaTag = `<meta name="offer" content="${name.trim()}">`;
    let priceTag = '';
    if (isCustomPrice && pIdx === 1) priceTag = `<meta name="RevTrackPrice" content="custom">\n`;
    else if (p) priceTag = `<meta name="RevTrackPrice" content="${p}">\n`;
    return `${usernameTag}\n${metaTag}\n${priceTag}<script src="https://d15dfsr886zcp9.cloudfront.net/thankYou_script.js" defer></script>`;
  };

  const callBookingScript = (name) =>
    `<meta name="username" content="${user ? username : ''}">
<meta name="offer" content="${name.trim()}">
<script src="https://d15dfsr886zcp9.cloudfront.net/callBookingThankYouScript.js" defer></script>`;

  const emailSignupScript = (name) =>
    `<meta name="username" content="${user ? username : ''}">
<meta name="offer" content="${name.trim()}">
<script src="https://d15dfsr886zcp9.cloudfront.net/emailSignupThankYouScript.js" defer></script>`;

  const funnelStepScript = (name, stepName) =>
    `<meta name="username" content="${user ? username : ''}">
<meta name="offer" content="${name.trim()}">
<meta name="funnelStepName" content="${stepName.trim()}">
<script src="https://d15dfsr886zcp9.cloudfront.net/customFunnelStepScript.js" defer></script>`;

  const getCodeSnippet = () => {
    if (stepType === 'landing') return { code: trackerScript(offerName), label: 'trackerScript' };
    if (stepType === 'call_booking') return { code: callBookingScript(offerName), label: 'callBookingScript' };
    if (stepType === 'email') return { code: emailSignupScript(offerName), label: 'emailSignupScript' };
    if (stepType === 'thank_you') {
      const code = priceIndex === 1
        ? thankYouScript(offerName, null, offer?.custom_price, priceIndex)
        : thankYouScript(offerName, price, offer?.custom_price, priceIndex);
      return { code, label: `thankYouScript${priceIndex || ''}` };
    }
    if (stepType === 'custom') return { code: funnelStepScript(offerName, title), label: 'funnelStepScript' };
    return null;
  };

  // For Lovable offers we can't load an external <script src>, so we build a prompt that
  // tells Lovable to inline the tracking code (meta tags + the script source) in a useEffect.
  const getLovablePrompt = () => {
    const entry = LOVABLE_SCRIPTS[stepType];
    const snippet = getCodeSnippet();
    if (!entry || !snippet) return null;
    // Meta tags: the normal snippet without the trailing <script src=...> line.
    const metaTags = snippet.code.replace(/<script\b[^>]*><\/script>\s*$/i, '').trim();
    // Inline JS: the raw script source made runnable (no module export, invoked on load).
    const js = `${entry.raw.replace(/export\s+function/g, 'function').trim()}\n\n${entry.init}();`;
    const prompt = `Add the following meta tags & scripts into the page inside a useEffect. Do not make any modifications to the code. In the useEffect, use Javascript to add the meta tags, do not add them directly into index.html. When the page loads, set the script to run..\n\n${metaTags}\n\n${js}`;
    return { prompt, label: `lovable-${snippet.label}` };
  };

  const getUrlLabel = () => {
    if (stepType === 'landing') return 'Your landing page URL';
    if (stepType === 'thank_you') return 'Your thank you page URL';
    if (stepType === 'call_booking') return 'Your call booking page URL';
    if (stepType === 'email') return 'Your email signup page URL';
    return 'Your page URL';
  };

  const displayTitle =
    stepType === 'custom' || stepType === 'call_booking' || stepType === 'email'
      ? `${title} Thank You Page`
      : title;

  const isStepVerified = () => {
    if (!offer) return false;
    
    if (stepType === 'landing') {
      return !!offer.landing_verified_at;
    } else if (stepType === 'call_booking') {
      return !!offer.call_booking_verified_at;
    } else if (stepType === 'thank_you') {
      if (priceIndex === 1) return !!offer.thank_you_verified_at;
      if (priceIndex === 2) return !!offer.thank_you2_verified_at;
      if (priceIndex === 3) return !!offer.thank_you3_verified_at;
    } else if (stepType === 'custom' && stepId) {
      const step = offer.funnel_steps?.find(s => s.id === stepId);
      return !!step?.verified_at;
    } else if (stepType === 'email' && stepId) {
      const step = offer.funnel_steps?.find(s => s.id === stepId);
      return !!step?.verified_at;
    }
    
    return false;
  };

  const isVerified = isStepVerified() || verificationResult?.valid || skipped;
  const { code: codeSnippet, label: codeLabel } = getCodeSnippet() || {};
  const isLovable = offer?.website_editor === 'lovable';
  const { prompt: lovablePrompt, label: lovablePromptLabel } = (isLovable && getLovablePrompt()) || {};


  const showFailure = verificationResult && !verificationResult.valid;

  const handleSkipVerification = async () => {
    console.log(username)
    try {
      const token = await getToken();
      await axios.post(`https://${SERVER_URL}/api/mark-step-verified`, {
        offerName: offer.name,
        stepType,
        stepName: title,
        priceIndex,
        username: username
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setSkipped(true);
      setVerificationResult(null);
      confetti({
        particleCount: 120,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#00d3f2', '#16a34a', '#ffffff', '#facc15'],
      });
      onVerified?.({ stepType, priceIndex, stepId });
    } catch (error) {
      console.error('Error skipping verification:', error);
    }
  };

  return (
    <div className={`funnel-block-card${isVerified ? ' funnel-block-card--verified' : ''}`}>
      {/* ── Compact row ── */}
      <div className="step-row" onClick={() => setExpanded(p => !p)}>
        <div className={`step-circle${isVerified ? ' step-circle--verified' : ''}`}>
          {isVerified ? <CheckCircle2 size={16} /> : <span>{stageNumber}</span>}
        </div>
        <div className="step-meta">
          <span className="step-meta-title">{displayTitle}</span>
          {description && <span className="step-meta-desc">{description}</span>}
        </div>
        <div className="step-row-right">
          {editMode && onEditPrice && !isEditingPrice && (
            <button
              className="step-edit-price-btn"
              onClick={e => { e.stopPropagation(); onEditPrice(); }}
              title="Edit price"
              aria-label="Edit price"
            >
              <Pencil size={13} />
            </button>
          )}
          {editMode && onDelete && (
            <button
              className="step-delete-btn"
              onClick={e => { e.stopPropagation(); onDelete(); }}
              title="Delete step"
              aria-label="Delete step"
            >
              <Trash2 size={13} />
            </button>
          )}
          <span className={`step-status-pill${isVerified ? ' step-status-pill--verified' : ''}`}>
            {isVerified ? 'Verified' : 'Not verified'}
          </span>
          <ChevronDown size={15} className={`step-chevron${expanded ? ' step-chevron--open' : ''}`} />
        </div>
      </div>

      {/* ── Inline price edit panel ── */}
      {isEditingPrice && (
        <div className="sc-price-edit-panel" onClick={(e) => e.stopPropagation()}>
          {priceIndex === 1 ? (
            <>
              <div className="fm-price-edit-card-row">
                {editingCustomPrice ? (
                  <span className="fm-price-variable-label">Variable</span>
                ) : (
                  <>
                    <span className="fm-price-currency">{getCurrencySymbol(currency)}</span>
                    <input
                      className="fm-price-input"
                      type="number"
                      min="0"
                      placeholder="0"
                      value={newPriceValue}
                      onChange={(e) => setNewPriceValue(e.target.value)}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') onSavePrice();
                        if (e.key === 'Escape') onCancelPrice();
                      }}
                    />
                  </>
                )}
                <div className="fm-price-edit-card-actions">
                  <button className="fm-price-confirm-btn" onClick={onSavePrice} title="Save">✓</button>
                  <button className="fm-price-cancel-btn" onClick={onCancelPrice} title="Cancel">✕</button>
                </div>
              </div>
              <label className="fm-add-price-variable-check">
                <input
                  type="checkbox"
                  checked={editingCustomPrice}
                  onChange={(e) => { setEditingCustomPrice(e.target.checked); setNewPriceValue(''); }}
                />
                Price is variable
              </label>
            </>
          ) : (
            <div className="fm-price-edit-card-row">
              <span className="fm-price-currency">{getCurrencySymbol(currency)}</span>
              <input
                className="fm-price-input"
                type="number"
                min="0"
                placeholder="0"
                value={newPriceValue}
                onChange={(e) => setNewPriceValue(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSavePrice();
                  if (e.key === 'Escape') onCancelPrice();
                }}
              />
              <div className="fm-price-edit-card-actions">
                <button className="fm-price-confirm-btn" onClick={onSavePrice} title="Save">✓</button>
                <button className="fm-price-cancel-btn" onClick={onCancelPrice} title="Cancel">✕</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Expanded content ── */}
      {expanded && (
        <div className="step-expanded-content">
          <div className="stage-content">

            {/* Lovable prompt section — shown instead of the code block for Lovable offers */}
            {lovablePrompt ? (
              <div className="sc-code-section">
                <div className="sc-code-header">
                  <span className="sc-field-label">Give Lovable this prompt for your {displayTitle}:</span>
                </div>
                <pre className="inline-code inline-code--scroll">{lovablePrompt}</pre>
                <button
                  className="sc-copy-btn"
                  onClick={() => copyToClipboard(lovablePrompt, lovablePromptLabel)}
                >
                  {copied === lovablePromptLabel ? '✓ Copied!' : 'Copy prompt'}
                </button>

                {stepType === 'landing' && (
                  <div className="sc-field-label">
                    <strong>Note:</strong> For the best results, also add this code to any page where users enter their email, for example the checkout page.
                  </div>
                )}
              </div>
            ) : (
              /* Code snippet section */
              codeSnippet && (
                <div className="sc-code-section">
                  <div className="sc-code-header">
                    <span className="sc-field-label">
                      Copy the code below and place it on your {displayTitle} as code
                    </span>
                    <button
                      className="sc-watch-link"
                      onClick={() => setShowVideoModal(true)}
                      aria-label="Watch how to paste the code"
                    >
                      Watch how →
                    </button>
                  </div>
                  <pre className="inline-code">{codeSnippet}</pre>
                  <button
                    className="sc-copy-btn"
                    onClick={() => copyToClipboard(codeSnippet, codeLabel)}
                  >
                    {copied === codeLabel ? '✓ Copied!' : 'Copy code'}
                  </button>

                  {stepType === 'landing' && (
                    <div className="sc-field-label">
                      <strong>Note:</strong> For the best results, also add this code to any page where users enter their email, for example the checkout page.
                    </div>
                  )}
                </div>
              )
            )}

            {/* custom_price instruction */}
            {stepType === 'thank_you' && offer?.custom_price && priceIndex === 1 && (
              <div className="custom-price-instruction">
                <span className="custom-price-instruction-icon">?</span>
                <p className="custom-price-instruction-text">
                  To track the price of each sale, add <strong>?rtprice=200</strong> to your thank you page URL (replace 200 with the sale amount).
                </p>
              </div>
            )}

            {/* Where to paste — shown for all steps except Lovable */}
            {!showFailure && !isLovable && (
              <div className="sc-paste-locations">
                <span className="sc-field-label">Where to paste it</span>
                
                <select 
                  className="sc-platform-select"
                  value={selectedPlatform}
                  onChange={(e) => setSelectedPlatform(e.target.value)}
                >
                  {PASTE_LOCATIONS.map(({ platform, value }) => (
                    <option key={value} value={value}>{platform}</option>
                  ))}
                </select>
                {selectedPlatform !== '' && (
                <div className="sc-paste-card sc-paste-card--prominent">
                  {PASTE_LOCATIONS.find(p => p.value === selectedPlatform)?.platform !== 'Other' &&
                    (<span className="sc-paste-platform">
                      {PASTE_LOCATIONS.find(p => p.value === selectedPlatform)?.platform}
                    </span>)
                  }

                  <span className="sc-paste-instructions">
                    {PASTE_LOCATIONS.find(p => p.value === selectedPlatform)?.instructions}
                  </span>
                </div>
                )}


                <p className="sc-ai-help-text">
                  Need more help?{' '}
                  <button
                    className="sc-ai-help-link"
                    onClick={() => window.dispatchEvent(new CustomEvent('open-ai-chatbot'))}
                  >
                    Get Support
                  </button>
                </p>
              </div>
            )}

            <div className="sc-check-setup">
                <h4 className="sc-check-setup-title">Check your setup</h4>

                <div className="sc-field-group">
                  <label className="sc-field-label">
                    {getUrlLabel()}
                    <span className="tooltip-container">
                      <Info size={11} style={{ verticalAlign: 'middle', marginLeft: 4, color: 'var(--Text-Body3)' }} />
                      <span className="tooltip-text">
                        This URL is used to verify that your tracking snippet is installed on the correct page. The URL you place here does not affect whether your tracking will work, and you may also place the code on other landing pages.
                      </span>
                    </span>
                  </label>
                  <div className="url-section">
                    <input
                      className={`url-input${urlIsInvalid ? ' url-input--error' : urlIsSaved ? ' url-input--valid' : ''}`}
                      placeholder="https://example.com/page"
                      value={localUrl}
                      onChange={(e) => {
                        const val = e.target.value;
                        setLocalUrl(val);
                        setUserHasEdited(true);
                        if (!val || isValidUrl(val)) {
                          onUrlUpdate(title, val, isCustomStep, stepId, priceIndex);
                        }
                      }}
                      onBlur={() => {
                        if (urlIsValid) {
                          onUrlUpdate(title, localUrl, isCustomStep, stepId, priceIndex);
                        }
                      }}
                    />
                  </div>
                  {urlIsInvalid && (
                    <p className="sc-url-feedback sc-url-feedback--error">Please enter a valid URL starting with https://</p>
                  )}
                  {urlIsSaved && (
                    <p className="sc-url-feedback sc-url-feedback--saved">✓ Saved</p>
                  )}
                </div>
              </div>

            {/* Failure state */}
            {showFailure && (
              <div className="sc-failure">
                <p className="sc-failure-heading">{verificationResult?.popupBlocked ? 'Automatic check blocked' : 'Code not detected'}</p>

                {verificationResult?.popupBlocked ? (
                  <div className="sc-error-card">
                    <span className="sc-error-card-title">This page blocks the automatic check</span>
                    <span className="sc-error-card-hint">We can&apos;t verify the script automatically. This site blocks the window we open for the check, so the result never comes back here. Tracking can still work. if you saw a "success result in the window, choose &quot;It&apos;s working, skip this&quot;.</span>
                  </div>
                ) : verificationResult?.error ? (
                  <div className="sc-error-card">
                    <span className="sc-error-card-title">Failed to open page</span>
                    <span className="sc-error-card-hint">We couldn't load your page URL. Some URLs are inaccessible to our verification tool. Please check the setup manually by creating a tracking link & clicking it to see if data appears in "Content". If so, you can click "It's working, skip this" below.</span>
                  </div>
                ) : verificationResult?.checks && verificationResult.checks.length > 0 ? (
                  <div className="sc-checks-list">
                    {verificationResult.checks.filter(c => !c.passed).map((check, idx) => (
                      <div key={idx} className="sc-error-card">
                        <span className="sc-error-card-title">{check.title}</span>
                        {check.hint && <span className="sc-error-card-hint">{check.hint}</span>}
                      </div>
                    ))}
                    {verificationResult.checks.filter(c => c.passed).map((check, idx) => (
                      <div key={`ok-${idx}`} className="sc-check-ok">
                        <CheckCircle2 size={13} />
                        <span>{check.title}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="sc-error-card">
                    <span className="sc-error-card-title">Setup incomplete</span>
                    <span className="sc-error-card-hint">Make sure you have pasted the entire snippet above as code, not text.</span>
                  </div>
                )}

                <div className="sc-verifier-caveat">
                  <Info size={13} className="sc-verifier-caveat-icon" />
                  <span>
                    The verification tool may sometimes be wrong. Please test manually by clicking a tracking link — if data appears in <strong>Content</strong>, your tracking is working and you can click <em>"It's working, skip this"</em> below.
                  </span>
                </div>
              </div>
            )}

            {/* Confirm / verify row */}
            {!showFailure ? (
              <div className="sc-confirm-row">
                <div>
                <p className="sc-confirm-label">Click to check your setup. Note: This is for testing purposes only and is not required for your offer to work.</p>
                </div>
                <button
                  className="sc-confirm-btn"
                  onClick={handleVerifySetup}
                  disabled={isVerifying || !url}
                >
                  {isVerifying
                    ? <><Loader2 size={14} className="button-icon animate-spin" /> Checking…</>
                    : 'Check setup'}
                </button>
              </div>
            ) : (
              <div className="sc-retry-row">
                <button className="sc-retry-btn" onClick={handleVerifySetup} disabled={isVerifying}>
                  {isVerifying ? <><Loader2 size={14} className="button-icon animate-spin" /> Checking…</> : 'Try again'}
                </button>
                <button className="sc-skip-btn" onClick={handleSkipVerification}>
                  It's working, skip this
                </button>
              </div>
            )}

            {showFailure && (
              <p className="sc-stuck-link">
                Still stuck?{' '}
                <a href="mailto:hi@getrevtrack.com">
                  Reach out to support
                </a>
              </p>
            )}

          </div>
        </div>
      )}

      {showVideoModal && (
        <div className="video-modal-overlay" onClick={() => setShowVideoModal(false)} role="dialog" aria-modal="true">
          <div className="video-modal" onClick={(e) => e.stopPropagation()}>
            <button className="video-modal-close" onClick={() => setShowVideoModal(false)} aria-label="Close video">✕</button>
            {/* eslint-disable-next-line react/no-unknown-property */}
            <wistia-player media-id="lnec13qx1p" aspect="1.7777777777777777" style={{width:'100%',display:'block'}} />
          </div>
        </div>
      )}
    </div>
  );
};

const formatFunnelCount = (n) => (Number(n) || 0).toLocaleString();

const formatFunnelRate = (rate) => {
  if (!isFinite(rate) || rate <= 0) return '0%';
  if (rate >= 99.95) return '100%';
  if (rate >= 10) return `${rate.toFixed(1)}%`;
  return `${rate.toFixed(2)}%`;
};

const formatFunnelRevenue = (amount, currency) => {
  const value = Number(amount) || 0;
  const formatted = value.toLocaleString(undefined, {
    maximumFractionDigits: value % 1 === 0 ? 0 : 2,
  });
  return `${getCurrencySymbol(currency)}${formatted}`;
};

const FUNNEL_ICONS = {
  play: PlayCircle,
  trend: TrendingUp,
  mail: Mail,
  phone: Phone,
  layers: Layers,
  dollar: DollarSign,
};

const FUNNEL_BENCHMARKS = {
  clicks: {
    threshold: 1,
    title: 'Low click rate',
    advice: 'a clearer CTA in your video, mentioning the offer earlier, or pinning a more obvious link.',
  },
  email: {
    threshold: 35,
    title: 'Low conversion rate',
    advice: 'simplifying your opt-in, strengthening the lead magnet, or reducing friction on the landing page.',
  },
  call: {
    threshold: 3,
    title: 'Low conversion rate',
    advice: 'fewer booking-form fields, a clearer calendar, or warming visitors up before asking for a call.',
  },
  sales: {
    threshold: 3,
    title: 'Low conversion rate',
    advice: 'tightening your offer, adding social proof, or making sure the landing page matches what you promised in the video.',
  },
  callSales: {
    threshold: 25,
    title: 'Low close rate',
    advice: 'qualifying leads before the call, tightening the offer on the call, or following up with no-shows.',
  },
  signups: {
    threshold: 35,
    title: 'Low conversion rate',
    advice: 'simplifying your opt-in, strengthening the lead magnet, or reducing friction on the landing page.',
  },
};

const getFunnelStepStats = (stats, stepId) =>
  stats?.funnel_steps?.[stepId] || stats?.funnel_steps?.[String(stepId)] || {};

const buildOfferFunnelSteps = (offer, currency, options = {}) => {
  const { statsOverride = null, omitViews = false } = options;
  const stats = statsOverride || offer.stats || {};
  const steps = [];

  if (!omitViews) {
    steps.push({
      key: 'views',
      label: 'VIDEO',
      value: stats.views || 0,
      sublabel: 'views',
      icon: 'play',
    });
  }

  steps.push({
    key: 'clicks',
    kind: 'clicks',
    label: 'TRAFFIC',
    value: stats.clicks || 0,
    sublabel: 'tracked clicks',
    icon: 'trend',
    verb: 'CLICKED',
  });

  const pushEmailStep = (key) => {
    steps.push({
      key,
      kind: 'email',
      label: 'EMAIL SIGNUPS',
      value: stats.email_signups || 0,
      sublabel: 'identified by email',
      icon: 'mail',
      verb: 'CONTINUED',
    });
  };

  const pushCustomStep = (funnelStep) => {
    const stepStats = getFunnelStepStats(stats, funnelStep.id);
    steps.push({
      key: `step-${funnelStep.id}`,
      kind: 'custom',
      label: (funnelStep.step_name || 'STEP').toUpperCase(),
      value: stepStats.conversions || 0,
      sublabel: 'conversions',
      icon: 'layers',
      verb: 'CONTINUED',
    });
  };

  const funnelSteps = (offer.funnel_steps || [])
    .slice()
    .sort((a, b) => a.step_order - b.step_order);

  funnelSteps
    .filter((step) => step.is_before_call && !step.is_post_thank_you)
    .forEach((step) => (step.is_email_step ? pushEmailStep(`email-${step.id}`) : pushCustomStep(step)));

  if (offer.call_booking_required) {
    steps.push({
      key: 'calls',
      kind: 'call',
      label: 'CALLS',
      value: stats.call_bookings || 0,
      sublabel: 'calls booked',
      icon: 'phone',
      verb: 'CONTINUED',
    });
  }

  funnelSteps
    .filter((step) => !step.is_before_call && !step.is_post_thank_you)
    .forEach((step) => (step.is_email_step ? pushEmailStep(`email-${step.id}`) : pushCustomStep(step)));

  const hasEmailStep = funnelSteps.some((step) => step.is_email_step);

  if (!offer.no_sale_step) {
    if (offer.offer_type === 'signup') {
      steps.push({
        key: 'sales',
        kind: 'signups',
        label: 'SIGNUPS',
        value: (stats.signups || 0) + (hasEmailStep ? 0 : (stats.email_signups || 0)),
        sublabel: 'email signups',
        icon: 'mail',
        verb: 'SIGNED UP',
        isGoal: true,
      });
    } else {
      steps.push({
        key: 'sales',
        kind: 'sales',
        label: 'CUSTOMERS',
        value: stats.sales || 0,
        sublabel: `${formatFunnelRevenue(stats.revenue, currency)} revenue`,
        icon: 'dollar',
        verb: 'BOUGHT',
        isGoal: true,
      });
    }
  }

  funnelSteps
    .filter((step) => step.is_post_thank_you)
    .forEach((step) => (step.is_email_step ? pushEmailStep(`email-post-${step.id}`) : pushCustomStep(step)));

  if (!steps.some((step) => step.isGoal)) {
    const lastConversion = [...steps].reverse().find((step) => step.key !== 'views' && step.key !== 'clicks');
    if (lastConversion) lastConversion.isGoal = true;
  }

  const views = omitViews ? 0 : (stats.views || 0);
  const clicks = stats.clicks || 0;

  for (let i = 1; i < steps.length; i++) {
    const prev = steps[i - 1].value;
    steps[i].rate = prev > 0 ? (steps[i].value / prev) * 100 : 0;

    if (omitViews) continue;

    const isCallToSales = steps[i].kind === 'sales' && steps[i - 1].kind === 'call';
    const bench = isCallToSales
      ? FUNNEL_BENCHMARKS.callSales
      : FUNNEL_BENCHMARKS[steps[i].kind];
    if (!bench) continue;
    const denom = isCallToSales
      ? prev
      : (steps[i].kind === 'clicks' ? views : clicks);
    const actual = denom > 0 ? (steps[i].value / denom) * 100 : 0;
    steps[i].isLow = denom > 0 && actual < bench.threshold;
    steps[i].warning = steps[i].isLow
      ? `${bench.title}: Consider ${bench.advice}`
      : null;
  }

  return steps;
};

const FunnelWarnIcon = ({ text }) => {
  const [pos, setPos] = useState(null);
  const iconRef = useRef(null);

  const show = () => {
    const rect = iconRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({ top: rect.top, left: rect.left + rect.width / 2 });
  };

  const hide = () => setPos(null);

  return (
    <span
      ref={iconRef}
      className="offer-funnel-warn"
      tabIndex={0}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      aria-label={text}
    >
      <AlertTriangle size={12} />
      {pos && (
        <span
          className="offer-funnel-tooltip"
          role="tooltip"
          style={{ top: pos.top, left: pos.left }}
        >
          {text}
        </span>
      )}
    </span>
  );
};

const OfferFunnelFlow = ({ steps, compact = false }) => (
  <div className={`offer-funnel${compact ? ' offer-funnel--compact' : ''}`}>
    {steps.map((step, index) => {
      const IconComponent = FUNNEL_ICONS[step.icon] || Layers;
      return (
        <Fragment key={step.key}>
          {index > 0 && (
            <div className={`offer-funnel-connector${step.isLow ? ' offer-funnel-connector--low' : ''}`}>
              <span className="offer-funnel-rate-row">
                <span className={`offer-funnel-rate${step.isLow ? ' offer-funnel-rate--low' : ''}`}>
                  {formatFunnelRate(step.rate)}
                </span>
                {step.isLow && step.warning && <FunnelWarnIcon text={step.warning} />}
              </span>
              <span className="offer-funnel-verb">{step.verb}</span>
              <ChevronRight size={compact ? 12 : 14} className="offer-funnel-arrow" />
            </div>
          )}
          <div className={`offer-funnel-card${step.isGoal ? ' offer-funnel-card--goal' : ''}`}>
            <div className="offer-funnel-card-top">
              <span className="offer-funnel-icon" aria-hidden="true">
                <IconComponent size={compact ? 12 : 14} />
              </span>
              <span className="offer-funnel-label">{step.label}</span>
            </div>
            <div className={`offer-funnel-value${step.isGoal ? ' offer-funnel-value--goal' : ''}`}>
              {formatFunnelCount(step.value)}
            </div>
            <div className={`offer-funnel-sub${step.isGoal ? ' offer-funnel-sub--goal' : ''}`}>
              {step.sublabel}
            </div>
          </div>
        </Fragment>
      );
    })}
  </div>
);

// Number of content pieces shown inline under an expanded traffic source.
// The "See all data" link below the list takes the user to the Content page.
const TOP_RESOURCES_LIMIT = 5;
// Traffic sources shown in the "By source" grid before "Show all" is clicked.
const TOP_SOURCES_LIMIT = 5;

const getSourceResources = (source) => (Array.isArray(source?.resources) ? source.resources : []);

// Ranking for the inline breakdown: customers first, then calls, emails,
// funnel-step conversions and finally clicks. "Untracked" always sinks to
// the bottom regardless of its numbers.
const resourceCustomers = (resource, isSignup) => (
  isSignup
    ? (Number(resource.signups) || 0) + (Number(resource.email_signups) || 0)
    : (Number(resource.sales) || 0)
);

const rankResources = (resources, isSignup) => {
  const num = (value) => Number(value) || 0;
  return [...resources].sort((a, b) => {
    if (a.id === 'untracked') return 1;
    if (b.id === 'untracked') return -1;
    return (
      resourceCustomers(b, isSignup) - resourceCustomers(a, isSignup)
      || num(b.call_bookings) - num(a.call_bookings)
      || num(b.email_signups) - num(a.email_signups)
      || num(b.step_conversions) - num(a.step_conversions)
      || num(b.clicks) - num(a.clicks)
    );
  });
};

const OfferFunnelSourceCard = ({ source, isExpanded, canExpand, isSignup, currency, onSelect, style }) => (
  <div className="offer-funnel-source-cell" style={style}>
    <button
      type="button"
      className={`offer-funnel-source-card${isExpanded ? ' is-expanded' : ''}${canExpand ? '' : ' is-static'}`}
      onClick={onSelect}
      aria-expanded={canExpand ? isExpanded : undefined}
      aria-controls={canExpand ? `source-resources-${source.id}` : undefined}
      title={canExpand ? `${isExpanded ? 'Hide' : 'Show'} top content for ${source.name}` : undefined}
    >
      <span className={`offer-funnel-source-toggle${canExpand ? '' : ' is-hidden'}`} aria-hidden="true">
        <ChevronRight size={14} />
      </span>
      <TrafficSourceLogo sourceId={source.id} name={source.name} />
      <span className="offer-funnel-source-card-body">
        <span className="offer-funnel-source-card-name">{source.name}</span>
      </span>
      <span className="offer-funnel-source-card-metrics">
        <span className="offer-funnel-source-card-revenue">
          {formatFunnelRevenue(source.revenue, currency)}
        </span>
        <span className="offer-funnel-source-card-sales">
          {formatFunnelCount(isSignup ? (source.signups || source.email_signups || 0) : (source.sales || 0))} {isSignup ? 'signups' : 'sales'}
        </span>
      </span>
    </button>
  </div>
);

// Inline breakdown rendered directly under its source row. Each resource row
// reuses the parent grid's column template so values line up with the step
// columns above.
const OfferFunnelResourcePanel = ({ source, offer, currency, isSignup, gridTemplateColumns, style }) => {
  const allResources = useMemo(
    () => rankResources(getSourceResources(source), isSignup),
    [source, isSignup]
  );
  const resources = allResources.slice(0, TOP_RESOURCES_LIMIT);
  const hiddenCount = allResources.length - resources.length;

  return (
    <div
      id={`source-resources-${source.id}`}
      className="offer-funnel-resource-panel"
      style={style}
      role="region"
      aria-label={`Top content for ${source.name}`}
    >
      {resources.map((resource) => {
        const resourceSteps = buildOfferFunnelSteps(offer, currency, {
          statsOverride: resource,
          omitViews: true,
        });
        return (
          <div key={resource.id} className="offer-funnel-resource-row" style={{ gridTemplateColumns }}>
            <span className="offer-funnel-resource-name" title={resource.name}>{resource.name}</span>
            {resourceSteps.map((step, colIndex) => (
              <span key={step.key} className="offer-funnel-resource-cell">
                <span className="offer-funnel-resource-value">{formatFunnelCount(step.value)}</span>
                <span className={`offer-funnel-resource-rate${colIndex === 0 ? ' is-muted' : ''}`}>
                  {colIndex === 0 ? 'visits' : formatFunnelRate(step.rate)}
                </span>
              </span>
            ))}
          </div>
        );
      })}
      <div className="offer-funnel-resource-footer">
        <Link to="/content" className="offer-funnel-resource-link">
          See all data
          <ChevronRight size={14} aria-hidden="true" />
        </Link>
        {hiddenCount > 0 && (
          <span className="offer-funnel-resource-footer-note">
            Showing top {resources.length} of {allResources.length}
          </span>
        )}
      </div>
    </div>
  );
};

const OfferFunnel = ({ offer, currency }) => {
  const [expandedSourceId, setExpandedSourceId] = useState(null);
  const [viewMode, setViewMode] = useState('source');
  const [showAllSources, setShowAllSources] = useState(false);

  const steps = useMemo(
    () => (offer ? buildOfferFunnelSteps(offer, currency) : []),
    [offer, currency]
  );

  const sources = useMemo(
    () => (Array.isArray(offer?.stats?.by_source) ? offer.stats.by_source : []),
    [offer]
  );

  useEffect(() => {
    setExpandedSourceId((prev) => (
      sources.some((source) => source.id === prev && getSourceResources(source).length > 0) ? prev : null
    ));
  }, [sources]);

  // Always start collapsed to the top sources when switching offers.
  useEffect(() => {
    setShowAllSources(false);
  }, [offer?.id]);

  const visibleSources = useMemo(
    () => (showAllSources ? sources : sources.slice(0, TOP_SOURCES_LIMIT)),
    [sources, showAllSources]
  );

  const hasFunnelData = useMemo(() => {
    const toNum = (value) => Number(value) || 0;
    return (
      steps.some((step) => step.key !== 'views' && toNum(step.value) > 0) ||
      sources.some((source) => (
        toNum(source.clicks) > 0 ||
        toNum(source.visits) > 0 ||
        toNum(source.revenue) > 0 ||
        toNum(source.sales) > 0 ||
        toNum(source.signups) > 0 ||
        toNum(source.email_signups) > 0
      ))
    );
  }, [steps, sources]);

  if (!offer || steps.length === 0 || !hasFunnelData) return null;

  const isSignup = offer.offer_type === 'signup';
  const showBreakdown = viewMode === 'source' && sources.length > 0;
  const breakdownColumns = steps.filter((step) => step.key !== 'views');
  const breakdownGridTemplateColumns = `minmax(150px, 260px) repeat(${breakdownColumns.length}, minmax(var(--offer-funnel-chip-width), 1fr))`;
  const breakdownGridStyle = {
    gridTemplateColumns: breakdownGridTemplateColumns,
    minWidth: `calc(150px + ${breakdownColumns.length} * (var(--offer-funnel-chip-width) + 10px))`,
  };
  const expandedIndex = visibleSources.findIndex((source) => source.id === expandedSourceId);
  const anyExpandable = visibleSources.some((source) => getSourceResources(source).length > 0);
  const hiddenSourceCount = sources.length - visibleSources.length;
  const canToggleSources = sources.length > TOP_SOURCES_LIMIT;
  // Row 1 holds the column headers. The expanded resource panel takes one
  // extra grid row, so every source below it shifts down by one.
  const sourceGridRow = (index) => index + 2 + (expandedIndex !== -1 && index > expandedIndex ? 1 : 0);

  const handleSourceSelect = (source) => {
    if (getSourceResources(source).length > 0) {
      setExpandedSourceId((prev) => (prev === source.id ? null : source.id));
    }
  };

  const handleToggleSources = () => {
    setShowAllSources((prev) => {
      // Collapsing back to the top list: drop an expansion that would be hidden.
      if (prev) {
        setExpandedSourceId((current) => (
          sources.slice(0, TOP_SOURCES_LIMIT).some((source) => source.id === current) ? current : null
        ));
      }
      return !prev;
    });
  };

  return (
    <div className="offer-funnel-panel">
      <div className="offer-funnel-panel-head">
        <div className="offer-funnel-panel-title-group">
          <div className="offer-funnel-panel-title">
            <span className="offer-funnel-panel-kicker">Funnel overview</span>
            <span className="offer-funnel-panel-name">{offer.name}</span>
          </div>
          {sources.length > 0 && (
            <div className="offer-funnel-view-toggle" role="tablist" aria-label="Funnel view">
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === 'overview'}
                className={`offer-funnel-view-option${viewMode === 'overview' ? ' is-selected' : ''}`}
                onClick={() => setViewMode('overview')}
              >
                Overview
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === 'source'}
                className={`offer-funnel-view-option${viewMode === 'source' ? ' is-selected' : ''}`}
                onClick={() => setViewMode('source')}
              >
                By source
              </button>
            </div>
          )}
        </div>
      </div>

      {showBreakdown && (
        <div className="offer-funnel-canvas" aria-label="Funnel flow by traffic source">
          <span className="offer-funnel-canvas-kicker">Traffic sources</span>
          <div className="offer-funnel-canvas-grid" style={breakdownGridStyle}>
            {breakdownColumns.map((step, colIndex) => (
              <span
                key={`head-${step.key}`}
                className="offer-funnel-col-head"
                style={{ gridRow: 1, gridColumn: colIndex + 2 }}
              >
                {step.label}
              </span>
            ))}
            {visibleSources.map((source, index) => {
              const isExpanded = expandedIndex === index;
              const canExpand = getSourceResources(source).length > 0;
              const sourceSteps = buildOfferFunnelSteps(offer, currency, {
                statsOverride: source,
                omitViews: true,
              });
              const gridRow = sourceGridRow(index);
              return (
                <Fragment key={source.id}>
                  <OfferFunnelSourceCard
                    source={source}
                    isExpanded={isExpanded}
                    canExpand={canExpand}
                    isSignup={isSignup}
                    currency={currency}
                    onSelect={() => handleSourceSelect(source)}
                    style={{ gridRow }}
                  />
                  {sourceSteps.map((step, colIndex) => {
                    return (
                      <div
                        key={step.key}
                        className="offer-funnel-lane"
                        style={{ gridRow, gridColumn: colIndex + 2 }}
                      >
                        <span className="offer-funnel-chip">
                          <span className="offer-funnel-chip-value">{formatFunnelCount(step.value)}</span>
                          <span className="offer-funnel-chip-sub">
                            {colIndex === 0 ? 'visits' : `${formatFunnelRate(step.rate)} conversion`}
                          </span>
                        </span>
                      </div>
                    );
                  })}
                  {isExpanded && (
                    <OfferFunnelResourcePanel
                      source={source}
                      offer={offer}
                      currency={currency}
                      isSignup={isSignup}
                      gridTemplateColumns={breakdownGridTemplateColumns}
                      style={{ gridRow: gridRow + 1 }}
                    />
                  )}
                </Fragment>
              );
            })}
          </div>
          {canToggleSources && (
            <div className="offer-funnel-sources-footer">
              <button
                type="button"
                className="offer-funnel-sources-toggle"
                onClick={handleToggleSources}
                aria-expanded={showAllSources}
              >
                {showAllSources ? 'Show top 5' : `Show all (${sources.length})`}
                <span className={`offer-funnel-sources-toggle-icon${showAllSources ? ' is-open' : ''}`} aria-hidden="true">
                  ▾
                </span>
              </button>
              {!showAllSources && (
                <span className="offer-funnel-sources-footer-note">
                  Showing top {visibleSources.length} of {sources.length} sources ({hiddenSourceCount} hidden)
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {!showBreakdown && (
        <div className="offer-funnel-steps">
          <OfferFunnelFlow steps={steps} />
        </div>
      )}
    </div>
  );
};

export const FunnelMapper = ({isProAccount, isFounder}) => {
  const { username, setIsProModalOpen } = useRevit();
  const { getToken } = useAuth();
  const { user } = useUser();
  const MAX_FREE_OFFERS = 5;
  const [offers, setOffers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeOffer, setActiveOffer] = useState(null);
  // Archived offers are kept in `offers` (so all historical data stays
  // intact) but rendered in a separate collapsed section below the
  // active list. The toggle is local to this component.
  const [showArchivedOffers, setShowArchivedOffers] = useState(false);
  const [newOfferName, setNewOfferName] = useState("");
  const [newOfferPrice, setNewOfferPrice] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [websiteEditor, setWebsiteEditor] = useState('other');
  const [offerSteps, setOfferSteps] = useState([
    { type: 'builtin', key: 'sale', active: true },
  ]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isDeletingOffer, setIsDeletingOffer] = useState(false);
  // Set while an archive / unarchive request is in flight so we can show
  // a small loading overlay. Kept separate from `isLoading` (which guards
  // the initial offers fetch) so the two never collide. The
  // `archivingTargetIsArchived` flag records the direction of the in-flight
  // request so the overlay can label itself correctly without having to
  // inspect offer state mid-request.
  const [isArchivingOffer, setIsArchivingOffer] = useState(false);
  const [archivingTargetIsArchived, setArchivingTargetIsArchived] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [originalCurrency, setOriginalCurrency] = useState('USD');
  const [hasReachedOfferLimit, setHasReachedOfferLimit] = useState(false);
  const [hasMultiplePrices, setHasMultiplePrices] = useState(false);
  const [conversionValue2, setConversionValue2] = useState('');
  const [hasThirdPrice, setHasThirdPrice] = useState(false);
  const [conversionValue3, setConversionValue3] = useState('');
  const [customPrice, setCustomPrice] = useState(false);
  const [savingCurrency, setSavingCurrency] = useState(false);
  const [showStepDeleteConfirmation, setShowStepDeleteConfirmation] = useState(false);
  const [stepToDelete, setStepToDelete] = useState(null);
  const [editingPrice, setEditingPrice] = useState(null);
  const [newPriceValue, setNewPriceValue] = useState('');
  const [isAddingPrice, setIsAddingPrice] = useState(false);
  const [isDeletingStep, setIsDeletingStep] = useState(false);
  const [editingCustomPrice, setEditingCustomPrice] = useState(false);
  const [copiedShopifyPixel, setCopiedShopifyPixel] = useState(false);
  const [isEditingOffer, setIsEditingOffer] = useState(false);
  const [addStepSlot, setAddStepSlot] = useState(null);
  const [addStepType, setAddStepType] = useState(null);
  const [isAddingStep, setIsAddingStep] = useState(false);
  const [videos, setVideos] = useState([]);
  const [isVideoDropdownOpen, setIsVideoDropdownOpen] = useState(false);
  const [isLoadingVideos, setIsLoadingVideos] = useState(false);
  const [selectedVideoIds, setSelectedVideoIds] = useState([]);
  const [customStartDate, setCustomStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [customEndDate, setCustomEndDate] = useState(new Date());
  const [timeframeOption, setTimeframeOption] = useState('all');
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const lifetimeStatsRef = useRef({});
  const timeframeRef = useRef('all');

  // Simple toast replacement
  const showToast = (message, type = 'info') => {
    console.log(`${type.toUpperCase()}: ${message}`);
    // You can replace this with your preferred toast implementation
  };

  // Function to invalidate cache after modifications
    const invalidateOffersCache = () => {
        if (username) {
            delete offersCache[username];
        }
    };

      // Modified useEffect to use caching
      useEffect(() => {
          const loadOffers = async () => {
              console.log(username, user);
              if (!username || !user) return;
              
              try {
                  setIsLoading(true);
                  
                  // Check if we have cached data that's not expired
                  const cachedData = offersCache[username];
                  const currentTime = new Date().getTime();
                  
                  if (cachedData && (currentTime - cachedData.timestamp < CACHE_EXPIRATION_TIME)) {
                      // Use cached data
                      console.log('Using cached offers data');
                      setOffers(cachedData.data.offers);
                      setCurrency(cachedData.data.currency);
                      setOriginalCurrency(cachedData.data.currency);
                      setHasReachedOfferLimit(!isProAccount && !isFounder && cachedData.data.offers.length >= MAX_FREE_OFFERS);
                      lifetimeStatsRef.current = Object.fromEntries(
                        (cachedData.data.offers || []).map((offer) => [offer.id, offer.stats])
                      );
                  } else {
                      // Fetch fresh data
                      console.log('Fetching fresh offers data');
                      const token = await getToken();
                      const result = await fetchOffers(username, token, { includeArchived: true });
                      
                      // Update state
                      setOffers(result.offers);
                      setCurrency(result.currency);
                      setOriginalCurrency(result.currency);
                      setHasReachedOfferLimit(!isProAccount && !isFounder && result.offers.length >= MAX_FREE_OFFERS);
                      lifetimeStatsRef.current = Object.fromEntries(
                        (result.offers || []).map((offer) => [offer.id, offer.stats])
                      );
                      
                      // Cache the result
                      offersCache[username] = {
                          data: result,
                          timestamp: currentTime
                      };
                  }
              } catch (error) {
                  console.error('Error loading offers:', error);
              } finally {
                  setIsLoading(false);
              }
          };
  
          loadOffers();
      }, [user, username, getToken, isProAccount, isFounder]);

  const toRangeIso = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    };
  };

  const applyStatsToOffers = (statsById) => {
    setOffers((prev) => prev.map((offer) => (
      statsById[offer.id] ? { ...offer, stats: statsById[offer.id] } : offer
    )));
    setActiveOffer((prev) => {
      if (!prev || !statsById[prev.id]) return prev;
      return { ...prev, stats: statsById[prev.id] };
    });
  };

  const restoreLifetimeStats = () => {
    applyStatsToOffers(lifetimeStatsRef.current || {});
  };

  const fetchDateRangeStats = async (start, end) => {
    if (!username) return;
    setIsStatsLoading(true);
    try {
      const token = await getToken();
      const { startDate, endDate } = toRangeIso(start, end);
      const result = await fetchOffersByDate(username, token, startDate, endDate);
      if (timeframeRef.current === 'all') return;
      const statsById = Object.fromEntries(
        (result.offers || []).map((offer) => [offer.id, offer.stats])
      );
      applyStatsToOffers(statsById);
    } catch (error) {
      console.error('Error loading offers by date:', error);
    } finally {
      setIsStatsLoading(false);
    }
  };

  useEffect(() => {
    if (isLoading || offers.length === 0 || timeframeOption === 'all') return;
    if (!customStartDate || !customEndDate) return;
    fetchDateRangeStats(customStartDate, customEndDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframeOption, customStartDate, customEndDate, isLoading]);

  // Automatically select the first offer when offers are loaded
  useEffect(() => {
      if (!isLoading && offers.length > 0 && !activeOffer) {
          setActiveOffer(offers[0]);
      }
  }, [isLoading, offers, activeOffer]);

  // Split the offers array into active + archived for rendering. The
  // archived offers remain in `offers` (so offer-related actions like
  // delete still work and tracked data is preserved), but they only
  // show up in the collapsed "Archived" section below the main list.
  const { activeOffers, archivedOffers } = useMemo(() => {
      const active = [];
      const archived = [];
      for (const offer of offers) {
          if (offer.is_archived) {
              archived.push(offer);
          } else {
              active.push(offer);
          }
      }
      return { activeOffers: active, archivedOffers: archived };
  }, [offers]);

  // Automatically open the dialog when there are 0 offers


  const handleDeleteOffer = async () => {
        if (!selectedOffer) return;
        setIsDeletingOffer(true);
        const token = await getToken();

        const offer = offers.find(offer => offer.name === selectedOffer);
        if (!offer) {
            alert('Offer not found');
            setIsDeletingOffer(false);
            return;
        }

        try {
            const response = await axios.delete(`https://${SERVER_URL}/api/delete-offer`, {
                params: {
                    username: username,
                    offerId: offer.id,
                },
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });

            if (response.status === 200) {
                alert('Offer deleted successfully');
                
                // Remove the deleted offer from state
                const updatedOffers = offers.filter(o => o.name !== selectedOffer);
                setOffers(updatedOffers);
                
                // If the deleted offer was the active offer, clear it or set to first remaining offer
                if (activeOffer && activeOffer.name === selectedOffer) {
                    setActiveOffer(updatedOffers.length > 0 ? updatedOffers[0] : null);
                }
                
                // Invalidate cache after deleting an offer
                invalidateOffersCache();
            } else {
                alert('Failed to delete offer');
            }
        } catch (error) {
            console.error('Error deleting offer:', error);
            alert('Error deleting offer');
        } finally {
            setIsDeletingOffer(false);
            setShowConfirmation(false);
            setSelectedOffer(null);
        }
    };

  // Flip the `is_archived` flag on a single offer via the backend, then
  // reflect the change in local state. Used for both archive and
  // unarchive from the offer list row buttons.
  const setOfferArchived = async (offer, archived) => {
      const token = await getToken();
      const endpoint = archived ? 'archive-offer' : 'unarchive-offer';
      setArchivingTargetIsArchived(archived);
      setIsArchivingOffer(true);
      try {
          const response = await axios.post(
              `https://${SERVER_URL}/api/${endpoint}`,
              { username, offerId: offer.id },
              { headers: { 'Authorization': `Bearer ${token}` } }
          );

          if (response.status === 200) {
              setOffers((prev) => prev.map((o) =>
                  o.id === offer.id ? { ...o, is_archived: archived } : o
              ));
              // Keep the active offer reference in sync — archived offers
              // should not be edited/displayed in the main panel.
              if (activeOffer && activeOffer.id === offer.id) {
                  setActiveOffer((prev) => prev ? { ...prev, is_archived: archived } : prev);
              }
              invalidateOffersCache();
          } else {
              alert(`Failed to ${archived ? 'archive' : 'unarchive'} offer`);
          }
      } catch (error) {
          console.error(`Error ${archived ? 'archiving' : 'unarchiving'} offer:`, error);
          alert(`Error ${archived ? 'archiving' : 'unarchiving'} offer`);
      } finally {
          setIsArchivingOffer(false);
      }
  };

  const handleArchiveOffer = (offer) => setOfferArchived(offer, true);
  const handleUnarchiveOffer = (offer) => setOfferArchived(offer, false);

  const addOffer = async () => {
    if (!newOfferName.trim()) {
      showToast("Please enter an offer name", "error");
      return;
    }

    if (newOfferName.includes('"')) {
      alert('Offer name cannot contain double quotes ("). Please remove them and try again.');
      return;
    }

    if (hasReachedOfferLimit) {
      return;
    }
    console.log('steps', offerSteps)

    // Derive values from unified offerSteps array
    const stepActive = (key) => offerSteps.find((s) => s.type === 'builtin' && s.key === key)?.active;
    const includeSale        = stepActive('sale');
    const includeCallBooking = stepActive('callBooking');
    const includeEmailSignup = stepActive('emailSignup');
    const emailIdx           = offerSteps.findIndex((s) => s.type === 'builtin' && s.key === 'emailSignup');
    const callIdx            = offerSteps.findIndex((s) => s.type === 'builtin' && s.key === 'callBooking');
    console.log(emailIdx, callIdx)
    const emailBeforeCall    = emailIdx < callIdx;
    console.log(emailBeforeCall)
    const derivedOfferType   = includeSale ? 'paid' : 'signup';
    const customStepItems    = offerSteps.filter((s) => s.type === 'custom' && s.name?.trim());
    const isShopify = websiteEditor === 'shopify';

    setIsLoading(true);

    try {
      const token = await getToken();
      const response = await axios.post(
        `https://${SERVER_URL}/api/add-offer`,
        {
          username: username,
          offerName: newOfferName.trim(),
          conversionValue: customPrice || isShopify ? null : (derivedOfferType === 'paid' ? (newOfferPrice ? parseFloat(newOfferPrice) : 0) : 0),
          conversionValue2: !customPrice && hasMultiplePrices && derivedOfferType === 'paid' && !isShopify ? (conversionValue2 || 0) : null,
          conversionValue3: !customPrice && hasThirdPrice && derivedOfferType === 'paid' && !isShopify ? (conversionValue3 || 0) : null,
          customPrice: customPrice || isShopify,
          callBookingRequired: includeCallBooking,
          websiteEditor: websiteEditor === 'shopify' ? 'shopify' : websiteEditor === 'lovable' ? 'lovable' : 'other',
          hasMultiplePrices: !customPrice && hasMultiplePrices && derivedOfferType === 'paid' && !isShopify,
          includeEmailSignupStep: includeEmailSignup,
          emailBeforeCall: emailBeforeCall || null,
          emailIdx: emailIdx || -1,
          noSaleStep: !includeSale,
          customSteps: customStepItems.map((cs, idx) => {
            // Determine if this step is before the call booking step
            const customStepIndex = offerSteps.indexOf(cs);     
            const isBeforeCallBooking = includeCallBooking && callIdx !== -1 && customStepIndex < callIdx;
     
            // Determine if this step is after the sale step
            const saleIdx = offerSteps.findIndex((s) => s.type === 'builtin' && s.key === 'sale');
            const isAfterSale = includeSale && saleIdx !== -1 && customStepIndex > saleIdx;
            
            return {
              name: cs.name.trim(),
              conversionValue: cs.price ? parseFloat(cs.price) : 0,
              isSaleStep: cs.price && parseFloat(cs.price) > 0,
              isBeforeCall: isBeforeCallBooking,
              isPostThankYou: isAfterSale,
              isEmailStep: false
            };
          })
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.offerId) {
        const newOfferId = response.data.offerId;
        
        // Fetch the fresh offer data from the server instead of manually constructing it
        const token = await getToken();
        const result = await axios.get(`https://${SERVER_URL}/api/offers/${username}`, {
          params: { includeArchived: 'true' },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        setOffers(result.data.offers);
        lifetimeStatsRef.current = Object.fromEntries(
          (result.data.offers || []).map((offer) => [offer.id, offer.stats])
        );
        
        // Find and set the newly created offer as active
        const newOffer = result.data.offers.find(offer => offer.id === newOfferId);
        if (newOffer) {
          setActiveOffer(newOffer);
        }
        
        setNewOfferName("");
        setNewOfferPrice("");
        setOfferSteps([
          { type: 'builtin', key: 'sale', active: true },
        ]);
        setHasMultiplePrices(false);
        setConversionValue2('');
        setHasThirdPrice(false);
        setConversionValue3('');
        setIsDialogOpen(false);
        showToast(`Offer created successfully`, "success");
        
        // Invalidate cache after adding an offer
        invalidateOffersCache();
        if (timeframeOption !== 'all') {
          fetchDateRangeStats(customStartDate, customEndDate);
        }
      } else {
        alert('Offer already exists for this user');
      }
    } catch (error) {
      console.error('Error adding offer:', error);
      alert('Error adding offer');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to save offer URL for specific step
  const saveOfferUrl = async (offerId, url, stepType, stepId, priceIndex) => {
    try {
      const token = await getToken();
      await axios.post(
        `https://${SERVER_URL}/api/save-offer-url`,
        {
          offerId: offerId,
          url: url,
          stepType: stepType,
          stepId,
          priceIndex
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      console.log('Offer URL saved successfully');
    } catch (error) {
      console.error('Error saving offer URL:', error);
    }
  };

  // Function to handle URL updates
  const handleUrlUpdate = (stageTitle, url, isCustomStep, stepId = null, priceIndex = 1) => {
    console.log('handleUrlUpdate', stageTitle, url, isCustomStep, stepId);
    if (!activeOffer) return;
    
    let stepType;
    if (isCustomStep) {
      stepType = 'custom';
    } else if (stageTitle === "Landing Page") {
      stepType = 'landing';
    } else if (stageTitle === "Call Booking") {
      stepType = 'call_booking';
    } else if (stageTitle === "Thank You Page" || stageTitle.includes("Thank You Page (Price")) {
      stepType = 'thank_you';
    }
    
    if (isCustomStep && stepId) {
      // For custom steps, update the funnel_steps array
      setOffers(prevOffers => prevOffers.map(offer =>
        offer.id === activeOffer.id
          ? {
              ...offer,
              funnel_steps: offer.funnel_steps?.map(step =>
                step.id === stepId ? { ...step, url: url } : step
              ) || []
            }
          : offer
      ));
      
      // Update activeOffer as well
      if (activeOffer.funnel_steps) {
        const updatedActiveOffer = {
          ...activeOffer,
          funnel_steps: activeOffer.funnel_steps.map(step =>
            step.id === stepId ? { ...step, url: url } : step
          )
        };
        setActiveOffer(updatedActiveOffer);
      }
    } else {
      // For regular steps, update the specific URL fields
      setOffers(prevOffers => prevOffers.map(offer =>
        offer.id === activeOffer.id
          ? {
              ...offer,
              landing_url: stepType === 'landing' ? url : offer.landing_url,
              call_booking_url: stepType === 'call_booking' ? url : offer.call_booking_url,
              thank_you_url: stepType === 'thank_you' && priceIndex === 1 ? url : offer.thank_you_url,
              thank_you_url2: stepType === 'thank_you' && priceIndex === 2 ? url : offer.thank_you_url2,
              thank_you_url3: stepType === 'thank_you' && priceIndex === 3 ? url : offer.thank_you_url3
            }
          : offer
      ));
      
      // Update activeOffer as well
      setActiveOffer(prevOffer => ({
        ...prevOffer,
        landing_url: stepType === 'landing' ? url : prevOffer.landing_url,
        call_booking_url: stepType === 'call_booking' ? url : prevOffer.call_booking_url,
        thank_you_url: stepType === 'thank_you' && priceIndex === 1 ? url : prevOffer.thank_you_url,
        thank_you_url2: stepType === 'thank_you' && priceIndex === 2 ? url : prevOffer.thank_you_url2,
        thank_you_url3: stepType === 'thank_you' && priceIndex === 3 ? url : prevOffer.thank_you_url3
      }));
    }
    
    // Save to database
    saveOfferUrl(activeOffer.id, url, stepType, stepId, priceIndex);
  };

  const handleStepVerified = ({ stepType, priceIndex, stepId }) => {
    const now = new Date().toISOString();
    const applyUpdate = (offer) => {
      if (stepType === 'landing') return { ...offer, landing_verified_at: now };
      if (stepType === 'call_booking') return { ...offer, call_booking_verified_at: now };
      if (stepType === 'thank_you') {
        if (priceIndex === 2) return { ...offer, thank_you2_verified_at: now };
        if (priceIndex === 3) return { ...offer, thank_you3_verified_at: now };
        return { ...offer, thank_you_verified_at: now };
      }
      if ((stepType === 'custom' || stepType === 'email') && stepId) {
        return {
          ...offer,
          funnel_steps: offer.funnel_steps?.map(s =>
            s.id === stepId ? { ...s, verified_at: now } : s
          ) || [],
        };
      }
      return offer;
    };
    setActiveOffer(prev => applyUpdate(prev));
    setOffers(prev => prev.map(o => o.id === activeOffer.id ? applyUpdate(o) : o));
  };

  const getShopifyPixelCode = (offerName, username) => {
    return `// RevTrack Shopify Pixel - ${offerName}
const REVTRACK_USERNAME = '${username}';
const REVTRACK_OFFER = '${offerName}';

const SERVER_URL = 'revenue-node-server.vercel.app';

// Cookie utilities
async function getCookie(name) {
    try {
        const value = await browser.cookie.get(name);
        return value || null;
    } catch (error) {
        console.error('[RevTrack] Error getting cookie:', error);
        return null;
    }
}

function pageLocation() {
    try {
        const location = init.context.document.location;
        return {
            hostname: location.hostname || '',
            pathname: location.pathname || '/',
            href: location.href || ''
        };
    } catch (error) {
        return { hostname: '', pathname: '/', href: '' };
    }
}

function pageHref() {
    return String(pageLocation().href || '').slice(0, 2000);
}

function isIpHost(hostname) {
    const parts = hostname.split('.');
    if (parts.length !== 4) return false;
    return parts.every(function (part) {
        if (!part || part.length > 3) return false;
        for (let i = 0; i < part.length; i++) {
            const code = part.charCodeAt(i);
            if (code < 48 || code > 57) return false;
        }
        return Number(part) <= 255;
    });
}

// Shopify only honors path and domain when they are part of the cookie string.
// A host-only cookie scoped to the current page is not sent to /checkouts.
function registrableDomain(hostname) {
    if (!hostname || hostname === 'localhost' || isIpHost(hostname)) return null;
    const parts = hostname.split('.');
    if (parts.length < 2) return null;
    const twoLevelTLDs = ['co.uk', 'com.au', 'co.nz', 'co.za', 'com.br', 'co.jp'];
    const lastTwo = parts.slice(-2).join('.');
    const lastThree = parts.slice(-3).join('.');
    return '.' + (twoLevelTLDs.indexOf(lastTwo) !== -1 && parts.length >= 3 ? lastThree : lastTwo);
}

function escapeCookieValue(value) {
    let out = '';
    const input = String(value || '');
    for (let i = 0; i < input.length; i++) {
        const code = input.charCodeAt(i);
        if (input.charAt(i) === ';' || code > 126) {
            out += String.fromCharCode(92) + 'u' + code.toString(16).padStart(4, '0');
        } else {
            out += input.charAt(i);
        }
    }
    return out;
}

async function usableCookieDomain(hostname) {
    const domain = registrableDomain(hostname);
    if (!domain) return null;
    const probe = 'revtrack_domain_probe';
    try {
        await browser.cookie.set(probe + '=1; path=/; max-age=60; SameSite=Lax; Secure; domain=' + domain);
        const ok = (await browser.cookie.get(probe)) === '1';
        await browser.cookie.set(probe + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax; Secure; domain=' + domain);
        return ok ? domain : null;
    } catch (error) {
        return null;
    }
}

async function expireHostCookie(name, path) {
    await browser.cookie.set(name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=' + path + '; SameSite=Lax');
}

async function setCookie(name, value, days) {
    try {
        const safeValue = escapeCookieValue(value);
        const maxAge = days * 24 * 60 * 60;
        const location = pageLocation();
        const domain = await usableCookieDomain(location.hostname);
        const pathname = location.pathname || '/';
        const directory = pathname.lastIndexOf('/') > 0 ? pathname.slice(0, pathname.lastIndexOf('/')) : '/';

        // Remove the page-scoped host-only cookie so it cannot shadow the domain cookie
        if (pathname !== '/') await expireHostCookie(name, pathname);
        if (directory !== '/' && directory !== pathname) await expireHostCookie(name, directory);
        if (domain) await expireHostCookie(name, '/');

        let cookie = name + '=' + safeValue + '; path=/; max-age=' + maxAge + '; SameSite=Lax; Secure';
        if (domain) cookie += '; domain=' + domain;
        await browser.cookie.set(cookie);

        if (domain && (await browser.cookie.get(name)) !== safeValue) {
            await browser.cookie.set(name + '=' + safeValue + '; path=/; max-age=' + maxAge + '; SameSite=Lax; Secure');
        }
    } catch (error) {
        console.error('[RevTrack] Error setting cookie:', error);
    }
}

function pageSearch() {
    try {
        return init.context.document.location.search || '';
    } catch (error) {
        return (window.location && window.location.search) || '';
    }
}

function pageNavigator() {
    try {
        return init.context.navigator || navigator;
    } catch (error) {
        return navigator;
    }
}

function generateLeadId() {
    return 'lead_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
}

function getResourceClicked() {
    const params = new URLSearchParams(pageSearch());

    if (params.has('video')) {
        const videoName = params.get('video');
        if (videoName && videoName.slice(-3) === '...') {
            console.log('[RevTrack] Video id broken, not tracking');
            return { type: 'NA', name: 'NA' };
        }
        return { type: 'video', name: videoName };
    } else if (params.has('rtemail')) {
        return { type: 'email', name: params.get('rtemail') };
    } else if (params.has('community')) {
        return { type: 'community', name: params.get('community') };
    } else if (params.has('channel')) {
        return { type: 'channel', name: params.get('channel') };
    } else if (params.has('twitter')) {
        return { type: 'twitter', name: params.get('twitter') };
    } else if (params.has('instagram')) {
        return { type: 'instagram', name: params.get('instagram') };
    } else if (params.has('threads')) {
        return { type: 'threads', name: params.get('threads') };
    } else if (params.has('linkedin')) {
        return { type: 'linkedin', name: params.get('linkedin') };
    } else if (params.has('podcast')) {
        return { type: 'podcast', name: params.get('podcast') };
    } else if (params.has('blog')) {
        return { type: 'blog', name: params.get('blog') };
    } else if (params.has('website')) {
        return { type: 'website', name: params.get('website') };
    } else if (params.has('tiktok')) {
        return { type: 'tiktok', name: params.get('tiktok') };
    } else if (params.has('ad')) {
        return { type: 'ad', name: params.get('ad') };
    } else if (params.has('telegram')) {
        return { type: 'telegram', name: params.get('telegram') };
    } else if (params.has('facebook')) {
        return { type: 'facebook', name: params.get('facebook') };
    } else if (params.has('discord')) {
        return { type: 'discord', name: params.get('discord') };
    } else if (params.has('pinComment')) {
        return { type: 'pinComment', name: params.get('pinComment') };
    } else if (params.has('live')) {
        return { type: 'live', name: params.get('live') };
    } else if (params.has('webinar')) {
        return { type: 'webinar', name: params.get('webinar') };
    } else if (params.has('leadMagnet')) {
        return { type: 'leadMagnet', name: params.get('leadMagnet') };
    } else if (params.has('qr')) {
        return { type: 'qr', name: params.get('qr') };
    } else {
        console.log('[RevTrack] No resource parameter found in URL');
        return { type: 'NA', name: 'NA' };
    }
}

function hasValidResource(resourceClicked) {
    return !!(
        resourceClicked
        && resourceClicked.name
        && resourceClicked.type
        && resourceClicked.name !== 'NA'
        && resourceClicked.type !== 'NA'
        && String(resourceClicked.name).trim() !== ''
        && String(resourceClicked.type).trim() !== ''
    );
}

function getClickTrackKey(resourceClicked) {
    return hasValidResource(resourceClicked)
        ? 'resourceClick_' + resourceClicked.name
        : 'resourceClick__no_resource';
}

function isDirectVisit(resourceClicked) {
    return !hasValidResource(resourceClicked) && !getInitialReferrer();
}

function isCheckoutPage() {
    const path = pageLocation().pathname || '';
    return path.indexOf('/checkouts') === 0 || path.indexOf('/checkout') === 0;
}

async function getSessionItem(key) {
    try {
        return await browser.sessionStorage.getItem(key);
    } catch (error) {
        return null;
    }
}

async function setSessionItem(key, value) {
    try {
        await browser.sessionStorage.setItem(key, value);
    } catch (error) {
        console.log('[RevTrack] Session storage unavailable, cannot persist', key);
    }
}

async function getLocalItem(key) {
    try {
        return await browser.localStorage.getItem(key);
    } catch (error) {
        return null;
    }
}

async function setLocalItem(key, value) {
    try {
        await browser.localStorage.setItem(key, value);
    } catch (error) {
        console.log('[RevTrack] Local storage unavailable, cannot persist', key);
    }
}

async function getIpAddress() {
    const controller = new AbortController();
    const timer = setTimeout(function () { controller.abort(); }, 2500);
    try {
        const response = await fetch('https://api.ipify.org?format=json', { signal: controller.signal });
        const data = await response.json();
        const ip = data && typeof data.ip === 'string' ? data.ip.trim() : '';
        return ip || null;
    } catch (error) {
        console.error('[RevTrack] Error fetching IP:', error);
        return null;
    } finally {
        clearTimeout(timer);
    }
}

function getDeviceType() {
    const nav = pageNavigator();
    const ua = (nav && nav.userAgent) || '';
    if (nav && nav.userAgentData && nav.userAgentData.mobile === true) return 'mobile';
    if (/iPad|Tablet|Android(?!.*Mobile)/i.test(ua)) return 'tablet';
    if (/Mobi|Android|iPhone|iPod/i.test(ua)) return 'mobile';
    return 'desktop';
}

function getLanguage() {
    const nav = pageNavigator();
    return (nav && (nav.language || (nav.languages && nav.languages[0]))) || null;
}

function referrerHost(value) {
    try {
        return new URL(value).hostname.replace(/^www\./i, '').toLowerCase();
    } catch (error) {
        return '';
    }
}

function isSameSiteReferrer(referrer) {
    const from = referrerHost(referrer);
    const page = String(pageLocation().hostname || '').replace(/^www\./i, '').toLowerCase();
    if (!from || !page) return false;
    return from === page || from.endsWith('.' + page) || page.endsWith('.' + from);
}

function getInitialReferrer() {
    let referrer = '';
    try {
        referrer = init.context.document.referrer || '';
    } catch (error) {
        referrer = '';
    }
    referrer = String(referrer).trim();
    if (!referrer || isSameSiteReferrer(referrer)) return null;
    return referrer.slice(0, 2000);
}

async function getVisitNumber() {
    const key = 'revtrack_visits:' + (pageLocation().pathname || '/');
    try {
        const current = parseInt((await getLocalItem(key)) || '0', 10);
        const next = (isFinite(current) && current > 0 ? current : 0) + 1;
        await setLocalItem(key, String(next));
        return next;
    } catch (error) {
        return 1;
    }
}

async function getClickContext() {
    return {
        referrer: getInitialReferrer(),
        device: getDeviceType(),
        visit_number: await getVisitNumber(),
        language: getLanguage()
    };
}

async function deleteCookie(name) {
    await expireHostCookie(name, '/');
    const domain = registrableDomain(pageLocation().hostname);
    if (domain) {
        await browser.cookie.set(name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax; Secure; domain=' + domain);
    }
}

async function canSetCookies() {
    const testCookieName = 'revtrack_test_cookie';
    await setCookie(testCookieName, 'test', 1);
    const canSet = (await getCookie(testCookieName)) === 'test';
    if (canSet) await deleteCookie(testCookieName);
    return canSet;
}

function readUserInfo(raw) {
    if (!raw) return null;
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
        return null;
    }
}

async function initializeUserInfo(customerUsername) {
    const userInfo = readUserInfo(await getCookie('userInfo'));

    if (userInfo) {
        if (!userInfo.lead_id) {
            userInfo.lead_id = generateLeadId();
            await setCookie('userInfo', JSON.stringify(userInfo), 30);
        }
        if (userInfo.email && !userInfo.emails) {
            userInfo.emails = [userInfo.email];
            delete userInfo.email;
            await setCookie('userInfo', JSON.stringify(userInfo), 30);
        } else if (!userInfo.emails) {
            userInfo.emails = [];
            await setCookie('userInfo', JSON.stringify(userInfo), 30);
        }
        return userInfo;
    }

    const created = {
        username: customerUsername,
        lead_id: generateLeadId(),
        emails: [],
        purchases: {}
    };
    await setCookie('userInfo', JSON.stringify(created), 30);
    return created;
}

// ============================================================================
// TRACKER SCRIPT LOGIC (runs on storefront pages)
// ============================================================================

async function isResourceClickAlreadyTracked(resourceClicked) {
    return (await getSessionItem(getClickTrackKey(resourceClicked))) === 'true';
}

async function sendResourceClickToServer(resourceClicked, userInfo) {
    const username = userInfo ? userInfo.username : REVTRACK_USERNAME;
    if (!username) {
        console.log('[RevTrack] No username available, cannot send resource click');
        return;
    }
    if (!REVTRACK_OFFER || !String(REVTRACK_OFFER).trim()) {
        console.log('[RevTrack] No offer available, cannot send resource click');
        return;
    }

    if (isDirectVisit(resourceClicked)) {
        console.log('[RevTrack] No resource or external referrer, click not sent');
        return;
    }

    const params = new URLSearchParams(pageSearch());
    const splitTest = params.get('split');
    const withResource = hasValidResource(resourceClicked);
    const ipAddress = await getIpAddress();
    const clickContext = await getClickContext();

    const payload = {
        page_url: pageHref(),
        username: username,
        offer: REVTRACK_OFFER,
        referrer: clickContext.referrer,
        device: clickContext.device,
        visit_number: clickContext.visit_number,
        language: clickContext.language
    };

    if (ipAddress) {
        payload.ip_address = ipAddress;
    } else {
        console.log('[RevTrack] No IP from ipify; server will use the request IP for this click');
    }

    if (withResource) payload.resourceClicked = resourceClicked;
    if (userInfo && userInfo.lead_id) payload.lead_id = userInfo.lead_id;
    if (splitTest) payload.splitTest = splitTest;

    console.log('[RevTrack] Sending resource click payload:', payload);

    fetch('https://' + SERVER_URL + '/api/resource-click/' + username, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    })
    .then(function (response) {
        return response.json().then(function (data) { return { ok: response.ok, data: data }; });
    })
    .then(async function (result) {
        console.log('[RevTrack] Resource click response:', result.data);
        if (result.ok && result.data && result.data.message === 'Click recorded successfully') {
            await setSessionItem(getClickTrackKey(resourceClicked), 'true');
        }
    })
    .catch(function (error) {
        console.error('[RevTrack] Error sending resource click:', error);
    });
}

async function handleTracking(resourceClicked) {
    if (!REVTRACK_USERNAME) {
        console.log('[RevTrack] No username available, cannot track');
        return;
    }
    if (!REVTRACK_OFFER || !String(REVTRACK_OFFER).trim()) {
        console.log('[RevTrack] No offer available, cannot track');
        return;
    }

    if (!(await canSetCookies())) {
        console.log('[RevTrack] Cookies cannot be set, sending resource click without a lead cookie');
        if (await isResourceClickAlreadyTracked(resourceClicked)) {
            console.log('[RevTrack] Resource click already tracked this session, skipping');
            return;
        }
        await sendResourceClickToServer(resourceClicked, null);
        return;
    }

    const userInfo = await initializeUserInfo(REVTRACK_USERNAME);

    if (hasValidResource(resourceClicked)) {
        userInfo.resourceClicked = resourceClicked;
    }

    if (!userInfo.initial_referrer) {
        const initialReferrer = getInitialReferrer();
        if (initialReferrer) userInfo.initial_referrer = initialReferrer;
    }

    const splitTest = new URLSearchParams(pageSearch()).get('split');
    if (splitTest) userInfo.splitTest = splitTest;

    await setCookie('userInfo', JSON.stringify(userInfo), 30);

    if (await isResourceClickAlreadyTracked(resourceClicked)) {
        console.log('[RevTrack] Resource click already tracked this session, skipping');
        return;
    }

    await sendResourceClickToServer(resourceClicked, userInfo);
}

async function runTrackerScript() {
    if (isCheckoutPage()) return;

    const existingUserInfo = await getCookie('userInfo');
    if (existingUserInfo) {
        await setCookie('userInfo', existingUserInfo, 30);
    }

    const resourceClicked = getResourceClicked();
    console.log('[RevTrack] Resource clicked:', resourceClicked);
    await handleTracking(resourceClicked);
}

// ============================================================================
// THANK YOU SCRIPT LOGIC (runs on checkout_completed)
// ============================================================================

function checkoutPrice(checkoutData) {
    const checkout = checkoutData && checkoutData.checkout;
    const total = checkout && checkout.totalPrice;
    return total && total.amount ? total.amount : null;
}

async function sendPurchaseByIp(username, offerName, price) {
    if (!username || !offerName) {
        console.log('[RevTrack] Cannot send IP-based purchase: missing username or offer');
        return;
    }

    const localStorageKey = 'purchase_' + offerName;
    if ((await getLocalItem(localStorageKey)) === 'true') {
        console.log('[RevTrack] Purchase for "' + offerName + '" already tracked');
        return;
    }

    const ipAddress = await getIpAddress();
    if (!ipAddress) {
        console.log('[RevTrack] Cannot send IP-based purchase: no IP address');
        return;
    }

    const payload = {
        page_url: pageHref(),
        offer: offerName,
        ip_address: ipAddress,
        device: getDeviceType(),
        language: getLanguage()
    };
    if (price) payload.price = price;

    console.log('[RevTrack] Sending IP-based purchase info:', payload);

    fetch('https://' + SERVER_URL + '/api/purchase-by-ip/' + username, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    })
    .then(function (response) {
        return response.json().then(function (data) { return { ok: response.ok, data: data }; });
    })
    .then(async function (result) {
        console.log('[RevTrack] IP-based purchase response:', result.data);
        if (result.ok) await setLocalItem(localStorageKey, 'true');
    })
    .catch(function (error) {
        console.error('[RevTrack] Error sending IP-based purchase info:', error);
    });
}

async function sendPurchaseInfoToServer(userInfo, checkoutData) {
    const username = (userInfo && userInfo.username) || REVTRACK_USERNAME;
    const offerName = REVTRACK_OFFER;

    if (!username) {
        console.log('[RevTrack] Username is missing.');
        return;
    }
    if (!offerName || !String(offerName).trim()) {
        console.log('[RevTrack] Offer name is missing.');
        return;
    }

    const localStorageKey = 'purchase_' + offerName;
    if ((await getLocalItem(localStorageKey)) === 'true') {
        console.log('[RevTrack] Purchase for "' + offerName + '" already tracked');
        return;
    }

    if (userInfo.purchases && userInfo.purchases[offerName]) {
        console.log('[RevTrack] Purchase for this offer has already been recorded.');
        await setLocalItem(localStorageKey, 'true');
        return;
    }

    const price = checkoutPrice(checkoutData);
    let resourceClicked = userInfo.resourceClicked;

    if (userInfo.funnelSteps && userInfo.funnelSteps[offerName] && userInfo.funnelSteps[offerName].length > 0) {
        const steps = userInfo.funnelSteps[offerName];
        const latestStep = steps[steps.length - 1];
        if (latestStep.resource) {
            resourceClicked = latestStep.resource;
            console.log('[RevTrack] Using resource from latest funnel step:', resourceClicked.name);
        }
    } else if (userInfo.callBookings && userInfo.callBookings[offerName]) {
        resourceClicked = userInfo.callBookings[offerName];
        console.log('[RevTrack] Using resource from call booking:', resourceClicked.name);
    }

    const validResource = hasValidResource(resourceClicked);
    if (!validResource) {
        console.log('[RevTrack] Resource clicked is missing; will try IP lookup on server:', resourceClicked);
    }

    const payload = {
        page_url: pageHref(),
        username: username,
        tag: userInfo.tag,
        offer: offerName,
        lead_id: userInfo.lead_id,
        device: getDeviceType(),
        language: getLanguage(),
        initial_referrer: userInfo.initial_referrer || null
    };

    const ipAddress = await getIpAddress();
    if (ipAddress) payload.ip_address = ipAddress;
    if (validResource) payload.resourceClicked = resourceClicked;
    if (price) payload.price = price;
    if (userInfo.splitTest) payload.splitTest = userInfo.splitTest;

    console.log('[RevTrack] Sending purchase info:', payload);

    fetch('https://' + SERVER_URL + '/api/purchase/' + username, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
    })
    .then(function (response) {
        return response.json().then(function (data) { return { ok: response.ok, data: data }; });
    })
    .then(async function (result) {
        console.log('[RevTrack] Purchase response:', result.data);
        if (!result.ok) return;
        if (!userInfo.username) userInfo.username = username;
        if (!userInfo.purchases) userInfo.purchases = {};
        userInfo.purchases[offerName] = true;
        await setCookie('userInfo', JSON.stringify(userInfo), 30);
        await setLocalItem(localStorageKey, 'true');
        console.log('[RevTrack] Purchase for ' + offerName + ' marked as tracked');
    })
    .catch(function (error) {
        console.error('[RevTrack] Error sending purchase info:', error);
    });
}

// ============================================================================
// SHOPIFY WEB PIXELS API INTEGRATION
// ============================================================================

analytics.subscribe('checkout_completed', async (event) => {
    console.log('[RevTrack] Checkout completed event detected:', event);

    const userInfo = readUserInfo(await getCookie('userInfo'));
    if (userInfo) {
        await sendPurchaseInfoToServer(userInfo, event.data);
        return;
    }

    console.log('[RevTrack] No userInfo found in cookies. Attempting IP-based purchase tracking');
    await sendPurchaseByIp(REVTRACK_USERNAME, REVTRACK_OFFER, checkoutPrice(event.data));
});

runTrackerScript();

console.log('[RevTrack] Shopify Pixel initialized - Username:', REVTRACK_USERNAME, 'Offer:', REVTRACK_OFFER);`;
  };

  const copyShopifyPixelCode = (offerName, username) => {
    const code = getShopifyPixelCode(offerName, username);
    navigator.clipboard.writeText(code).then(() => {
      setCopiedShopifyPixel(true);
      setTimeout(() => setCopiedShopifyPixel(false), 2000);
    });
  };

  // Function to calculate stage number based on step position in funnel
  const getStageNumber = (stepType, stepId = null, priceIndex = 1) => {
    let stageNumber = 1; // Start with landing page
    
    // Landing page is always 1
    if (stepType === 'landing') return 1;
    
    stageNumber++; // Move past landing page
    
    // Add custom steps before call booking
    if (activeOffer?.funnel_steps) {
      const beforeCallSteps = activeOffer.funnel_steps
        .filter(step => step.is_before_call)
        .sort((a, b) => a.step_order - b.step_order);
      
      // Check if this is one of the before-call steps
      if (stepType === 'custom' && stepId) {
        const stepIndex = beforeCallSteps.findIndex(step => step.id === stepId);
        if (stepIndex !== -1) {
          return stageNumber + stepIndex;
        }
      }
      
      stageNumber += beforeCallSteps.length;
    }
    
    // Call booking step
    if (stepType === 'call_booking') return stageNumber;
    if (activeOffer?.call_booking_required) stageNumber++;
    
    // Add custom steps after call booking but before thank you (mid-funnel)
    if (activeOffer?.funnel_steps) {
      const midFunnelSteps = activeOffer.funnel_steps
        .filter(step => !step.is_before_call && !step.is_post_thank_you)
        .sort((a, b) => a.step_order - b.step_order);
      
      // Check if this is one of the mid-funnel steps
      if (stepType === 'custom' && stepId) {
        const stepIndex = midFunnelSteps.findIndex(step => step.id === stepId);
        if (stepIndex !== -1) {
          return stageNumber + stepIndex;
        }
      }
      
      stageNumber += midFunnelSteps.length;
    }
    
    // Thank you pages (only if not no_sale_step)
    if (stepType === 'thank_you') {
      if (priceIndex === 1) return stageNumber;
      if (priceIndex === 2) return stageNumber + 1;
      if (priceIndex === 3) return stageNumber + 2;
    }
    
    // Move past thank you pages
    if (!activeOffer?.no_sale_step) {
      stageNumber++; // Main thank you page
      if (activeOffer?.has_multiple_prices && activeOffer?.conversion_value2) stageNumber++;
      if (activeOffer?.conversion_value3) stageNumber++;
    }
    
    // Post-thank-you custom steps
    if (activeOffer?.funnel_steps) {
      const postThankYouSteps = activeOffer.funnel_steps
        .filter(step => step.is_post_thank_you)
        .sort((a, b) => a.step_order - b.step_order);
      
      // Check if this is one of the post-thank-you steps
      if (stepType === 'custom' && stepId) {
        const stepIndex = postThankYouSteps.findIndex(step => step.id === stepId);
        if (stepIndex !== -1) {
          return stageNumber + stepIndex;
        }
      }
    }
    
    return stageNumber;
  };
  // Clear verification results when active offer changes
  useEffect(() => {
    // and they will clear their verification state
  }, [activeOffer]);

  const handleSaveCurrency = async () => {
    setSavingCurrency(true);
    try {
      const token = await getToken();
      await axios.post(
        `https://${SERVER_URL}/api/save-currency`,
        {
          username: username,
          currency: currency
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      setOriginalCurrency(currency);
      
      // Invalidate cache after currency change
      invalidateOffersCache();
    } catch (error) {
      console.error('Error saving currency:', error);
      showToast('Error saving currency preference', 'error');
    } finally {
      setSavingCurrency(false);
    }
  };

  const handleDeleteStepClick = (stepId, stepType, priceId) => {
    setStepToDelete({ stepId, stepType, priceId });
    setShowStepDeleteConfirmation(true);
  };

  const confirmDeleteStep = async () => {
    if (!stepToDelete) return;
    
    setIsDeletingStep(true);
    try {
      const token = await getToken();
      await axios.delete(
        `https://${SERVER_URL}/api/delete-step`,
        {
          data: {
            offerId: activeOffer.id,
            stepType: stepToDelete.stepType,
            stepId: stepToDelete.stepId,
            priceId: stepToDelete.priceId
          },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Reload offers to get updated data
      const result = await axios.get(`https://${SERVER_URL}/api/offers/${username}`, {
        params: { includeArchived: 'true' },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setOffers(result.data.offers);
      lifetimeStatsRef.current = Object.fromEntries(
        (result.data.offers || []).map((offer) => [offer.id, offer.stats])
      );
      
      // Update active offer with the new data
      const updatedActiveOffer = result.data.offers.find(offer => offer.id === activeOffer.id);
      if (updatedActiveOffer) {
        setActiveOffer(updatedActiveOffer);
      }
      
      // Invalidate cache after deleting step
      invalidateOffersCache();
      if (timeframeOption !== 'all') {
        fetchDateRangeStats(customStartDate, customEndDate);
      }
      showToast("Step deleted successfully", "success");
    } catch (error) {
      console.error('Error deleting step:', error);
      showToast("Error deleting step", "error");
    } finally {
      setIsDeletingStep(false);
      setShowStepDeleteConfirmation(false);
      setStepToDelete(null);
    }
  };

  const deleteStep = (stepId, stepType, priceId) => {
    handleDeleteStepClick(stepId, stepType, priceId);
  };

  const reloadOffers = async () => {
    const token = await getToken();
    const result = await axios.get(`https://${SERVER_URL}/api/offers/${username}`, {
      params: { includeArchived: 'true' },
      headers: { 'Authorization': `Bearer ${token}` },
    });
    setOffers(result.data.offers);
    lifetimeStatsRef.current = Object.fromEntries(
      (result.data.offers || []).map((offer) => [offer.id, offer.stats])
    );
    const updated = result.data.offers.find(o => o.id === activeOffer.id);
    if (updated) setActiveOffer(updated);
    invalidateOffersCache();
    if (timeframeOption !== 'all') {
      await fetchDateRangeStats(customStartDate, customEndDate);
    }
  };

  const handleTimeframeChange = (option) => {
    timeframeRef.current = option;
    setTimeframeOption(option);

    if (option === 'all') {
      restoreLifetimeStats();
      return;
    }

    if (option !== 'custom') {
      const endDate = new Date();
      const startDate = new Date();

      switch (option) {
        case '30days':
          startDate.setDate(endDate.getDate() - 30);
          break;
        case '90days':
          startDate.setDate(endDate.getDate() - 90);
          break;
        case '6months':
          startDate.setMonth(endDate.getMonth() - 6);
          break;
        case '1year':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
        case 'yeartodate': {
          const currentYear = new Date().getFullYear();
          startDate.setFullYear(currentYear, 0, 1);
          startDate.setHours(0, 0, 0, 0);
          break;
        }
        case 'monthtodate':
          startDate.setDate(1);
          startDate.setHours(0, 0, 0, 0);
          break;
        case 'lastmonth':
          endDate.setDate(0);
          endDate.setHours(23, 59, 59, 999);
          startDate.setMonth(endDate.getMonth());
          startDate.setDate(1);
          startDate.setHours(0, 0, 0, 0);
          break;
        default:
          startDate.setDate(endDate.getDate() - 30);
          break;
      }

      setCustomStartDate(startDate);
      setCustomEndDate(endDate);
    }
  };

  const handleAddStep = async (slot, type, name, price) => {
    setIsAddingStep(true);
    try {
      const token = await getToken();
      if (type === 'call_booking') {
        await axios.post(`https://${SERVER_URL}/api/add-call-booking-step`,
          { offerId: activeOffer.id, stepIndex: slot.stepIndex },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else if (type === 'email') {
        await axios.post(`https://${SERVER_URL}/api/add-email-signup-step`,
          { offerId: activeOffer.id, stepIndex: slot.stepIndex, isBeforeCall: slot.isBeforeCall, isPostThankYou: slot.isPostThankYou },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else if (type === 'custom') {
        await axios.post(`https://${SERVER_URL}/api/add-custom-step`,
          { offerId: activeOffer.id, stepIndex: slot.stepIndex, stepName: name, conversionValue: price ? parseFloat(price) : 0, isBeforeCall: slot.isBeforeCall, isPostThankYou: slot.isPostThankYou },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      await reloadOffers();
      setAddStepSlot(null);
      setAddStepType(null);
      showToast('Step added successfully', 'success');
    } catch (error) {
      console.error('Error adding step:', error);
      showToast('Error adding step', 'error');
    } finally {
      setIsAddingStep(false);
    }
  };

  const handleEditPrice = (offerId, priceNumber, currentValue) => {
    setEditingPrice({ offerId, priceNumber });
    setNewPriceValue(currentValue !== null && currentValue !== undefined ? currentValue.toString() : '');
    setEditingCustomPrice(activeOffer?.custom_price || false);
  };

  const handleSavePrice = async () => {
    if (!editingPrice) return;
    
    const { offerId, priceNumber } = editingPrice;
    const token = await getToken();
    
    try {
      let endpoint = '';
      let payload = { offerId, username };
      
      if (priceNumber === 1) {
        endpoint = 'edit-offer';
        payload.newName = activeOffer.name;
        payload.newConversionValue = editingCustomPrice ? null : parseFloat(newPriceValue);
        payload.customPrice = editingCustomPrice;
      } else if (priceNumber === 2) {
        endpoint = 'edit-offer-price2';
        payload.newConversionValue2 = parseFloat(newPriceValue);
      } else if (priceNumber === 3) {
        endpoint = 'edit-offer-price3';
        payload.newConversionValue3 = parseFloat(newPriceValue);
      }
      
      await axios.post(
        `https://${SERVER_URL}/api/${endpoint}`,
        payload,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      await reloadOffers();
      setEditingPrice(null);
      setNewPriceValue('');
      setEditingCustomPrice(false);
      showToast("Price updated successfully", "success");
    } catch (error) {
      console.error('Error updating price:', error);
      showToast("Error updating price", "error");
    }
  };

  const handleSaveNewPrice = async () => {
    if (!newPriceValue || parseFloat(newPriceValue) <= 0) {
      showToast("Please enter a valid price", "error");
      return;
    }

    try {
      const token = await getToken();
      await axios.post(
        `https://${SERVER_URL}/api/add-price`,
        {
          offerId: activeOffer.id,
          price: newPriceValue,
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Update the offer with the new price
      const updatedOffer = { ...activeOffer };
      if (!activeOffer.has_multiple_prices) {
        updatedOffer.has_multiple_prices = true;
        updatedOffer.conversion_value2 = parseFloat(newPriceValue);
      } else if (!activeOffer.conversion_value3) {
        updatedOffer.conversion_value3 = parseFloat(newPriceValue);
      }
      
      setActiveOffer(updatedOffer);
      setOffers(offers.map(offer => 
        offer.id === activeOffer.id ? updatedOffer : offer
      ));
      
      setIsAddingPrice(false);
      setNewPriceValue('');
      invalidateOffersCache();
      showToast("Price added successfully", "success");
    } catch (error) {
      console.error('Error adding price:', error);
      showToast("Error adding price", "error");
    }
  };

  const handleCancelAddPrice = () => {
    setIsAddingPrice(false);
    setNewPriceValue('');
  };

  const handleCancelEdit = () => {
    setEditingPrice(null);
    setNewPriceValue('');
    setEditingCustomPrice(false);
  };

  const getOfferStatus = (offer) => {
    if (offer.website_editor === 'shopify') return 'live';

    // Must have landing verified
    if (!offer.landing_verified_at) return 'setup';

    // Call booking step (if required)
    if (offer.call_booking_required && !offer.call_booking_verified_at) return 'setup';

    // Custom funnel steps
    if (offer.funnel_steps?.length) {
      for (const step of offer.funnel_steps) {
        if (!step.verified_at) return 'setup';
      }
    }

    // Thank you / sale steps
    if (!offer.no_sale_step) {
      if (offer.has_multiple_prices) {
        if (!offer.thank_you_verified_at) return 'setup';
        if (offer.conversion_value2 && !offer.thank_you2_verified_at) return 'setup';
        if (offer.conversion_value3 && !offer.thank_you3_verified_at) return 'setup';
      } else {
        if (!offer.thank_you_verified_at) return 'setup';
      }
    }

    return 'live';
  };

  const isEmailOffer = (offer) =>
    offer.offer_type === 'signup' ||
    (!!(offer.funnel_steps?.some(s => s.is_email_step)) && !!offer.no_sale_step);

  const getOfferPriceBadge = (offer) => {
    if (offer.website_editor === 'shopify') return 'Shopify';
    if (isEmailOffer(offer)) return <FontAwesomeIcon icon={faEnvelope} />;
    if (offer.custom_price) return 'Variable Price';
    return offer.conversion_value ? `${getCurrencySymbol(currency)}${offer.conversion_value}` : '—';
  };

  // ── Edit-mode slot computation ──────────────────────────
  const editSlots = (() => {
    if (!isEditingOffer || !activeOffer) return {};
    const allSteps = (activeOffer.funnel_steps || []).slice().sort((a, b) => a.step_order - b.step_order);
    const preCallSteps = allSteps.filter(s => s.is_before_call && !s.is_post_thank_you);
    const midSteps     = allSteps.filter(s => !s.is_before_call && !s.is_post_thank_you);
    const postTYSteps  = allSteps.filter(s => s.is_post_thank_you);
    const s = {};
    const firstOrder = allSteps.length > 0 ? allSteps[0].step_order : 1;
    s['after-landing'] = { stepIndex: firstOrder, isBeforeCall: !!activeOffer.call_booking_required, isPostThankYou: false };
    preCallSteps.forEach(step => {
      s[`after-pre-${step.id}`] = { stepIndex: step.step_order + 1, isBeforeCall: true, isPostThankYou: false };
    });
    if (activeOffer.call_booking_required) {
      const firstMidOrder = midSteps.length > 0 ? midSteps[0].step_order
        : (preCallSteps.length > 0 ? preCallSteps[preCallSteps.length - 1].step_order + 1 : 1);
      s['after-call-booking'] = { stepIndex: firstMidOrder, isBeforeCall: false, isPostThankYou: false };
    }
    midSteps.forEach(step => {
      s[`after-mid-${step.id}`] = { stepIndex: step.step_order + 1, isBeforeCall: false, isPostThankYou: false };
    });
    const firstPostTYOrder = postTYSteps.length > 0 ? postTYSteps[0].step_order
      : (allSteps.length > 0 ? allSteps[allSteps.length - 1].step_order + 1 : 1);
    s['after-ty'] = { stepIndex: firstPostTYOrder, isBeforeCall: false, isPostThankYou: true };
    postTYSteps.forEach(step => {
      s[`after-post-ty-${step.id}`] = { stepIndex: step.step_order + 1, isBeforeCall: false, isPostThankYou: true };
    });
    return s;
  })();

  const hasEmailSignup = activeOffer?.funnel_steps?.some(s => s.is_email_step) || false;

  // Non-PRO users can only have landing + thank you. If any extra step already exists, block adding more.

  const fetchAllVideos = async () => {
    if (videos.length > 0) {
      setIsVideoDropdownOpen(!isVideoDropdownOpen);
      return;
    }
    
    setIsLoadingVideos(true);
    setIsVideoDropdownOpen(true);
    try {
      const token = await getToken();
      const response = await axios.get(
        `https://${SERVER_URL}/api/get-all-videos/${username}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setVideos(response.data.videos);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
      showToast('Error fetching videos', 'error');
    } finally {
      setIsLoadingVideos(false);
    }
  };

  const toggleVideoSelection = (videoId) => {
    setSelectedVideoIds(prev => 
      prev.includes(videoId) 
        ? prev.filter(id => id !== videoId)
        : [...prev, videoId]
    );
  };
  const nonProLimitReached = !isProAccount && (
    !!activeOffer?.call_booking_required ||
    (activeOffer?.funnel_steps?.length > 0)
  );

  const renderAddStep = (slotKey) => {
    if (!isEditingOffer || !editSlots[slotKey]) return null;
    const slot = editSlots[slotKey];
    const isActive = addStepSlot?.key === slotKey;
    return (
      <AddStepSection
        key={`add-${slotKey}`}
        slot={slot}
        isActive={isActive}
        activeType={addStepType}
        isProAccount={isProAccount}
        hasEmailSignup={hasEmailSignup}
        hasCallBooking={!!activeOffer?.call_booking_required}
        isLoading={isAddingStep}
        disabled={nonProLimitReached}
        onOpen={() => { 
          if (!nonProLimitReached) {
            setAddStepSlot({ ...slot, key: slotKey }); 
            setAddStepType(null); 
          }
        }}
        onSelectType={async (type) => {
          if (type === 'custom') { setAddStepType('custom'); }
          else { await handleAddStep({ ...slot, key: slotKey }, type); }
        }}
        onSubmitCustom={async (name, price) => { await handleAddStep({ ...slot, key: slotKey }, 'custom', name, price); }}
        onClose={() => { setAddStepSlot(null); setAddStepType(null); }}
        currency={currency}
      />
    );
  };

  return (
    <div className="funnel-mapper">
      {isLoading ? (
        <div className="loading-overlay">
          <div className="loading-text">Loading...</div>
        </div>
      ) : isArchivingOffer ? (
        // Shown while an archive / unarchive request is in flight so the
        // user gets clear feedback that the click registered. The
        // backend call is fast, so this is usually a flash. The label
        // uses the direction captured when the request started, not the
        // current `activeOffer` (which may not match the row that was
        // actually clicked).
        <div className="loading-overlay">
          <div className="loading-text">
            <Loader2 size={22} className="button-icon animate-spin" style={{ marginRight: 8, verticalAlign: 'middle' }} />
            {archivingTargetIsArchived ? 'Archiving offer…' : 'Unarchiving offer…'}
          </div>
        </div>
      ) : offers.length === 0 ? (
        /* ── Empty state: create first offer ── */
        <div className="fm-empty-state">
          <div className="inline-offer-form">
            <div className="form-container">
              <div className="form-header">
                <h2 className="dialog-title">Create Your First Offer</h2>
                <p className="dialog-description">Get started by creating your first offer.</p>
              </div>
              <OfferBuilderForm
                steps={offerSteps}
                onStepsChange={setOfferSteps}
                offerName={newOfferName}
                onOfferNameChange={setNewOfferName}
                offerPrice={newOfferPrice}
                onOfferPriceChange={setNewOfferPrice}
                hasSecondPrice={hasMultiplePrices}
                onHasSecondPriceChange={setHasMultiplePrices}
                secondPrice={conversionValue2}
                onSecondPriceChange={setConversionValue2}
                hasThirdPrice={hasThirdPrice}
                onHasThirdPriceChange={setHasThirdPrice}
                thirdPrice={conversionValue3}
                onThirdPriceChange={setConversionValue3}
                customPrice={customPrice}
                onCustomPriceChange={setCustomPrice}
                currency={currency}
                onSubmit={addOffer}
                isLoading={isLoading}
                isProAccount={isProAccount}
                websiteEditor={websiteEditor}
                onWebsiteEditorChange={setWebsiteEditor}
                username={username}
                isOpen={true}
              />
            </div>
          </div>
        </div>
      ) : (
        /* ── Two-column layout ── */
        <div className="fm-layout">
          {/* ── Sidebar ── */}
          <aside className="fm-sidebar">
            <div className="fm-currency-row">
              <span className="fm-currency-label">Account Currency</span>
              <select
                id="currency-select"
                className="fm-currency-select"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
                <option value="CAD">CAD (C$)</option>
                <option value="AUD">AUD (A$)</option>
                <option value="JPY">JPY (¥)</option>
                <option value="CHF">CHF (Fr)</option>
                <option value="CNY">CNY (¥)</option>
                <option value="INR">INR (₹)</option>
                <option value="BRL">BRL (R$)</option>
                <option value="MXN">MXN ($)</option>
                <option value="SGD">SGD (S$)</option>
                <option value="NZD">NZD (NZ$)</option>
                <option value="HKD">HKD (HK$)</option>
                <option value="SEK">SEK (kr)</option>
                <option value="NOK">NOK (kr)</option>
                <option value="DKK">DKK (kr)</option>
                <option value="ZAR">ZAR (R)</option>
                <option value="RUB">RUB (₽)</option>
                <option value="AED">AED (dh)</option>
              </select>
              {currency !== originalCurrency && (
                <button onClick={handleSaveCurrency} disabled={savingCurrency} className="save-currency-btn">
                  {savingCurrency ? 'Saving...' : 'Save Currency'}
                </button>
              )}
            </div>
            <p className="fm-section-label">YOUR OFFERS</p>
            <div className="fm-offer-list">
              {activeOffers.map((offer) => {
                const isActive = activeOffer?.id === offer.id;
                const status = getOfferStatus(offer);
                return (
                  <div
                    key={offer.id}
                    className={`fm-offer-item${isActive ? ' fm-offer-item--active' : ''}`}
                    onClick={() => setActiveOffer(offer)}
                  >
                    <span className="fm-offer-item-name" title={offer.name}>
                      {offer.offer_type === 'signup' && <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 6 }} />}
                      {offer.name}
                      {offer.website_editor === 'lovable' && (
                        <img src={lovableLogo} alt="Lovable" className="fm-editor-logo" />
                      )}
                      {offer.website_editor === 'shopify' && (
                        <img src={shopifyLogo} alt="Shopify" className="fm-editor-logo" />
                      )}
                    </span>
                    <div className="fm-offer-item-actions">
                      {/* Archive is hover-revealed so the active list stays
                          visually quiet — keep the price badge front and
                          centre. The button stops propagation so clicking
                          it doesn't switch the active offer. */}
                      <button
                        className="fm-offer-archive-btn"
                        title="Archive offer"
                        aria-label="Archive offer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArchiveOffer(offer);
                        }}
                      >
                        <Archive size={14} />
                      </button>
                      <span className="fm-offer-badge fm-offer-badge--price">{getOfferPriceBadge(offer)}</span>
                    </div>
                  </div>
                );
              })}
              {activeOffers.length === 0 && (
                <div className="fm-offer-empty-hint">
                  No active offers. Create one below or unarchive from the Archived section.
                </div>
              )}
            </div>
            {!hasReachedOfferLimit ? (
              <button className="fm-add-offer-btn" onClick={() => setIsDialogOpen(true)}>
                + Add new offer
              </button>
            ) : (
              <>
                <button className="fm-add-offer-btn fm-add-offer-btn--locked" disabled>
                  + Add new offer <span className="pro-badge" style={{ marginLeft: 6 }}>PRO</span>
                </button>
                <div className="offer-limit-message">
                  <p>You've reached the max offers. <button className="upgrade-prompt" onClick={() => openBillingPortal(username)}>Upgrade to Pro</button></p>
                </div>
              </>
            )}

            {/* Archived offers section — collapsed by default, mirrors the
                active row layout but offers an unarchive action instead. */}
            {archivedOffers.length > 0 && (
              <div className="fm-archived-section">
                <button
                  className="fm-archived-toggle"
                  aria-expanded={showArchivedOffers}
                  onClick={() => setShowArchivedOffers((v) => !v)}
                >
                  <ChevronRight
                    size={14}
                    className={`fm-archived-chevron${showArchivedOffers ? ' fm-archived-chevron--open' : ''}`}
                  />
                  Archived
                  <span className="fm-archived-count">{archivedOffers.length}</span>
                </button>
                {showArchivedOffers && (
                  <div className="fm-offer-list fm-archived-list">
                    {archivedOffers.map((offer) => {
                      const isActive = activeOffer?.id === offer.id;
                      return (
                        <div
                          key={offer.id}
                          className={`fm-offer-item fm-offer-item--archived${isActive ? ' fm-offer-item--active' : ''}`}
                          onClick={() => setActiveOffer(offer)}
                        >
                          <span className="fm-offer-item-name" title={offer.name}>
                            {offer.offer_type === 'signup' && <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: 6 }} />}
                            {offer.name}
                          </span>
                          <div className="fm-offer-item-actions">
                            <button
                              className="fm-offer-archive-btn"
                              title="Unarchive offer"
                              aria-label="Unarchive offer"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUnarchiveOffer(offer);
                              }}
                            >
                              <ArchiveRestore size={14} />
                            </button>
                            <span className="fm-offer-badge fm-offer-badge--archived">Archived</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            <div className={`dialog-overlay ${isDialogOpen ? '' : 'hidden'}`} onClick={() => setIsDialogOpen(false)}>
              <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
                <div className="dialog-header">
                  <h2 className="dialog-title">Create New Offer</h2>
                </div>
                <OfferBuilderForm
                  steps={offerSteps}
                  onStepsChange={setOfferSteps}
                  offerName={newOfferName}
                  onOfferNameChange={setNewOfferName}
                  offerPrice={newOfferPrice}
                  onOfferPriceChange={setNewOfferPrice}
                  hasSecondPrice={hasMultiplePrices}
                  onHasSecondPriceChange={setHasMultiplePrices}
                  secondPrice={conversionValue2}
                  onSecondPriceChange={setConversionValue2}
                  hasThirdPrice={hasThirdPrice}
                  onHasThirdPriceChange={setHasThirdPrice}
                  thirdPrice={conversionValue3}
                  onThirdPriceChange={setConversionValue3}
                  customPrice={customPrice}
                  onCustomPriceChange={setCustomPrice}
                  currency={currency}
                  onSubmit={addOffer}
                  isLoading={isLoading}
                  isProAccount={isProAccount}
                  websiteEditor={websiteEditor}
                  onWebsiteEditorChange={setWebsiteEditor}
                  username={username}
                  isOpen={isDialogOpen}
                />
              </div>
            </div>
          </aside>
          {/* ── Main panel ── */}
          <main className="fm-main">
            {/* Adding-step overlay */}
            {isAddingStep && (
              <div className="fm-adding-step-overlay">
                <div className="fm-adding-step-card">
                  <Loader2 size={22} className="fm-adding-step-spinner" />
                  <span className="fm-adding-step-label">Adding step…</span>
                </div>
              </div>
            )}
            {activeOffer && (
              <>
                <div className="fm-offer-header">
                  <div className="fm-offer-header-left">
                    <h2 className="fm-offer-title">
                      {activeOffer.name}
                      {activeOffer.website_editor === 'lovable' && (
                        <img src={lovableLogo} alt="Lovable" className="fm-editor-logo fm-editor-logo--title" />
                      )}
                      {activeOffer.website_editor === 'shopify' && (
                        <img src={shopifyLogo} alt="Shopify" className="fm-editor-logo fm-editor-logo--title" />
                      )}
                    </h2>
                    <div className="fm-offer-subtitle-row">
                      {activeOffer.website_editor !== 'shopify' ? (
                        <p className="fm-offer-subtitle">To set up tracking for your offer, click each of the following steps and copy the given code to the relevant page.</p>
                      ) : (
                        <p className="fm-offer-subtitle">To set up tracking for your offer in Shopify, follow the instructions below.</p>
                      )}
                      <button
                        className="delete-offer-button"
                        onClick={(e) => { e.stopPropagation(); setSelectedOffer(activeOffer.name); setShowConfirmation(true); }}
                      >
                        <Icon name="delete" size={20} color="var(--Delete-Red)" />
                      </button>
                    </div>
                  </div>
                </div>

                {activeOffer.website_editor !== 'shopify' && (
                  <>
                    <div className="fm-date-filter">
                      <label>Date Range:</label>
                      <div className="fm-timeframe-container">
                        <select
                          value={timeframeOption}
                          onChange={(e) => handleTimeframeChange(e.target.value)}
                          className="fm-timeframe-selector"
                        >
                          <option value="all">All time</option>
                          <option value="30days">Last 30 Days</option>
                          <option value="monthtodate">Month to Date</option>
                          <option value="lastmonth">Previous Month</option>
                          <option value="90days">Last 90 Days</option>
                          <option value="6months">Last 6 Months</option>
                          <option value="1year">Last 1 Year</option>
                          <option value="yeartodate">Year to Date</option>
                          <option value="custom">Custom</option>
                        </select>
                        {timeframeOption === 'custom' && (
                          <div className="fm-date-range">
                            <DatePicker
                              selected={customStartDate}
                              onChange={(date) => setCustomStartDate(date)}
                              selectsStart
                              startDate={customStartDate}
                              endDate={customEndDate}
                              placeholderText="Start Date"
                              dateFormat="MMM d, yyyy"
                              maxDate={new Date()}
                            />
                            <span className="fm-date-separator">to</span>
                            <DatePicker
                              selected={customEndDate}
                              onChange={(date) => setCustomEndDate(date)}
                              selectsEnd
                              startDate={customStartDate}
                              endDate={customEndDate}
                              minDate={customStartDate}
                              maxDate={new Date()}
                              placeholderText="End Date"
                              dateFormat="MMM d, yyyy"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className={`offer-funnel-wrap${isStatsLoading ? ' offer-funnel-wrap--loading' : ''}`}>
                      <OfferFunnel offer={activeOffer} currency={currency} />
                    </div>
                  </>
                )}

                <div className="fm-steps-list">
                  {activeOffer.website_editor !== 'shopify' ? (
                    <>
                      {/* 1. Landing page (never deletable) */}
                      <StepCard
                        id="landing-page"
                        title="Landing Page"
                        description="Your main opt-in or sales page"
                        url={activeOffer.landing_url || ""}
                        onDelete={null}
                        onUrlUpdate={handleUrlUpdate}
                        onVerified={handleStepVerified}
                        isLast={false}
                        isCustomStep={false}
                        stageNumber={getStageNumber('landing')}
                        offerName={activeOffer.name}
                        offer={activeOffer}
                        price={activeOffer.price}
                        activeOfferId={activeOffer.id}
                        stepType="landing"
                        currency={currency}
                        editMode={isEditingOffer}
                      />
                      {renderAddStep('after-landing')}

                      {/* Pre-call custom steps */}
                      {activeOffer.is_custom && activeOffer.funnel_steps &&
                        activeOffer.funnel_steps
                          .slice().sort((a, b) => a.step_order - b.step_order)
                          .filter(step => step.is_before_call && !step.is_post_thank_you)
                          .map(step => (
                            <div key={step.id}>
                              <StepCard
                                id={`custom-step-${step.id}`}
                                title={step.step_name}
                                url={step.url || ""}
                                onDelete={() => deleteStep(step.id, 'custom')}
                                onUrlUpdate={handleUrlUpdate}
                                onVerified={handleStepVerified}
                                isCustomStep={true}
                                stageNumber={getStageNumber('custom', step.id)}
                                offerName={activeOffer.name}
                                price={step.conversion_value}
                                activeOfferId={activeOffer.id}
                                offer={activeOffer}
                                stepId={step.id}
                                stepType={step.is_email_step ? "email" : "custom"}
                                currency={currency}
                                editMode={isEditingOffer}
                              />
                              {renderAddStep(`after-pre-${step.id}`)}
                            </div>
                          ))
                      }

                      {/* Call booking step */}
                      {activeOffer.call_booking_required && (
                        <div>
                          <StepCard
                            id="call-booking-thank-you"
                            title="Call Booking"
                            description="Track when visitors book a call"
                            url={activeOffer.call_booking_url || ""}
                            onDelete={() => deleteStep(null, 'call_booking')}
                            onUrlUpdate={handleUrlUpdate}
                            onVerified={handleStepVerified}
                            isCustomStep={false}
                            stageNumber={getStageNumber('call_booking')}
                            offerName={activeOffer.name}
                            price={activeOffer.price}
                            activeOfferId={activeOffer.id}
                            offer={activeOffer}
                            stepType="call_booking"
                            currency={currency}
                            editMode={isEditingOffer}
                          />
                          {renderAddStep('after-call-booking')}
                        </div>
                      )}

                      {/* Mid-funnel custom steps */}
                      {activeOffer.is_custom && activeOffer.funnel_steps &&
                        activeOffer.funnel_steps
                          .slice().sort((a, b) => a.step_order - b.step_order)
                          .filter(step => !step.is_before_call && !step.is_post_thank_you)
                          .map(step => (
                            <div key={step.id}>
                              <StepCard
                                id={`custom-step-${step.id}`}
                                title={step.step_name}
                                url={step.url || ""}
                                onDelete={() => deleteStep(step.id, 'custom')}
                                onUrlUpdate={handleUrlUpdate}
                                onVerified={handleStepVerified}
                                isCustomStep={true}
                                stageNumber={getStageNumber('custom', step.id)}
                                offerName={activeOffer.name}
                                price={step.conversion_value}
                                activeOfferId={activeOffer.id}
                                offer={activeOffer}
                                stepId={step.id}
                                stepType={step.is_email_step ? "email" : "custom"}
                                currency={currency}
                                editMode={isEditingOffer}
                              />
                              {renderAddStep(`after-mid-${step.id}`)}
                            </div>
                          ))
                      }

                      {/* Thank you blocks */}
                      {!activeOffer.no_sale_step && (
                        activeOffer.has_multiple_prices ? (
                          <div className="multiple-thank-you-blocks">
                            <StepCard
                              id="thank-you-page-1"
                              description="Shown after purchase or opt-in"
                              title={activeOffer.custom_price ? `Thank You Page (Price 1: Variable)` : `Thank You Page (Price 1: ${getCurrencySymbol(currency)}${activeOffer.conversion_value})`}
                              url={activeOffer.thank_you_url || ""}
                              onDelete={null}
                              onUrlUpdate={handleUrlUpdate}
                              onVerified={handleStepVerified}
                              isCustomStep={false}
                              stageNumber={getStageNumber('thank_you')}
                              offerName={activeOffer.name}
                              price={activeOffer.conversion_value}
                              activeOfferId={activeOffer.id}
                              offer={activeOffer}
                              priceIndex={1}
                              stepType="thank_you"
                              currency={currency}
                              onEditPrice={() => handleEditPrice(activeOffer.id, 1, activeOffer.custom_price ? null : activeOffer.conversion_value)}
                              isEditingPrice={editingPrice?.offerId === activeOffer.id && editingPrice?.priceNumber === 1}
                              newPriceValue={newPriceValue}
                              setNewPriceValue={setNewPriceValue}
                              editingCustomPrice={editingCustomPrice}
                              setEditingCustomPrice={setEditingCustomPrice}
                              onSavePrice={handleSavePrice}
                              onCancelPrice={handleCancelEdit}
                              editMode={isEditingOffer}
                              isProAccount={isProAccount}
                            />
                            {activeOffer.conversion_value2 && (
                              <StepCard
                                id="thank-you-page-2"
                                description="Shown after purchase or opt-in for price 2"
                                title={`Thank You Page (Price 2: ${getCurrencySymbol(currency)}${activeOffer.conversion_value2})`}
                                url={activeOffer.thank_you_url2 || ""}
                                onDelete={() => deleteStep(null, 'thank_you', 2)}
                                onUrlUpdate={handleUrlUpdate}
                                onVerified={handleStepVerified}
                                isCustomStep={false}
                                stageNumber={getStageNumber('thank_you')}
                                offerName={activeOffer.name}
                                price={activeOffer.conversion_value2}
                                activeOfferId={activeOffer.id}
                                offer={activeOffer}
                                priceIndex={2}
                                stepType="thank_you"
                                currency={currency}
                                onEditPrice={() => handleEditPrice(activeOffer.id, 2, activeOffer.conversion_value2)}
                                isEditingPrice={editingPrice?.offerId === activeOffer.id && editingPrice?.priceNumber === 2}
                                newPriceValue={newPriceValue}
                                setNewPriceValue={setNewPriceValue}
                                editingCustomPrice={editingCustomPrice}
                                setEditingCustomPrice={setEditingCustomPrice}
                                onSavePrice={handleSavePrice}
                                onCancelPrice={handleCancelEdit}
                                editMode={isEditingOffer}
                                isProAccount={isProAccount}
                              />
                            )}
                            {activeOffer.conversion_value3 && (
                              <StepCard
                                id="thank-you-page-3"
                                title={`Thank You Page (Price 3: ${getCurrencySymbol(currency)}${activeOffer.conversion_value3})`}
                                description="Shown after purchase or opt-in for price 3"
                                url={activeOffer.thank_you_url3 || ""}
                                onDelete={() => deleteStep(null, 'thank_you', 3)}
                                onUrlUpdate={handleUrlUpdate}
                                onVerified={handleStepVerified}
                                isCustomStep={false}
                                stageNumber={getStageNumber('thank_you')}
                                offerName={activeOffer.name}
                                price={activeOffer.conversion_value3}
                                activeOfferId={activeOffer.id}
                                offer={activeOffer}
                                priceIndex={3}
                                stepType="thank_you"
                                currency={currency}
                                onEditPrice={() => handleEditPrice(activeOffer.id, 3, activeOffer.conversion_value3)}
                                isEditingPrice={editingPrice?.offerId === activeOffer.id && editingPrice?.priceNumber === 3}
                                newPriceValue={newPriceValue}
                                setNewPriceValue={setNewPriceValue}
                                editingCustomPrice={editingCustomPrice}
                                setEditingCustomPrice={setEditingCustomPrice}
                                onSavePrice={handleSavePrice}
                                onCancelPrice={handleCancelEdit}
                                editMode={isEditingOffer}
                                isProAccount={isProAccount}
                              />
                            )}
                            {isEditingOffer && !isAddingPrice && !activeOffer.conversion_value3 && activeOffer.offer_type !== 'signup' && (
                              <button
                                className={`fm-add-price-below-btn${!(isProAccount || isFounder) ? ' fm-add-price-below-btn--disabled' : ''}`}
                                onClick={() => { 
                                  if (isProAccount || isFounder) {
                                    setIsAddingPrice(true); 
                                    setNewPriceValue(''); 
                                  } else {
                                    setIsProModalOpen(true);
                                  }
                                }}
                                type="button"
                                title={!(isProAccount || isFounder) ? "Adding prices is a Pro feature — click to upgrade" : "Add price"}
                              >
                                <Plus size={13} />
                                Add price
                                {!(isProAccount || isFounder) && <span className="offer-form-pro-badge">PRO</span>}
                              </button>
                            )}
                            {isAddingPrice && (
                              <div className="fm-add-price-inline fm-add-price-inline--below">
                                <span className="fm-price-currency">{getCurrencySymbol(currency)}</span>
                                <input
                                  className="fm-price-input"
                                  type="number"
                                  min="0"
                                  placeholder="0"
                                  value={newPriceValue}
                                  onChange={(e) => setNewPriceValue(e.target.value)}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveNewPrice();
                                    if (e.key === 'Escape') handleCancelAddPrice();
                                  }}
                                />
                                <button className="fm-price-confirm-btn" onClick={handleSaveNewPrice} title="Save">✓</button>
                                <button className="fm-price-cancel-btn" onClick={handleCancelAddPrice} title="Cancel">✕</button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <StepCard
                              id="thank-you-page"
                              title={activeOffer.custom_price ? `Thank You Page (Price: Variable)` : activeOffer.conversion_value ? `Thank You Page (Price: ${getCurrencySymbol(currency)}${activeOffer.conversion_value})` : `Thank You Page`}
                              description="Shown after purchase or opt-in"
                              url={activeOffer.thank_you_url || ""}
                              onDelete={null}
                              onUrlUpdate={handleUrlUpdate}
                              onVerified={handleStepVerified}
                              isCustomStep={false}
                              stageNumber={getStageNumber('thank_you')}
                              offerName={activeOffer.name}
                              price={activeOffer.conversion_value}
                              priceIndex={1}
                              activeOfferId={activeOffer.id}
                              offer={activeOffer}
                              stepType="thank_you"
                              currency={currency}
                              onEditPrice={() => handleEditPrice(activeOffer.id, 1, activeOffer.custom_price ? null : activeOffer.conversion_value)}
                              isEditingPrice={editingPrice?.offerId === activeOffer.id && editingPrice?.priceNumber === 1}
                              newPriceValue={newPriceValue}
                              setNewPriceValue={setNewPriceValue}
                              editingCustomPrice={editingCustomPrice}
                              setEditingCustomPrice={setEditingCustomPrice}
                              onSavePrice={handleSavePrice}
                              onCancelPrice={handleCancelEdit}
                              editMode={isEditingOffer}
                              isProAccount={isProAccount}
                            />
                            {isEditingOffer && !isAddingPrice && activeOffer.offer_type !== 'signup' && (
                              <button
                                className={`fm-add-price-below-btn${!(isProAccount || isFounder) ? ' fm-add-price-below-btn--disabled' : ''}`}
                                onClick={() => { 
                                  if (isProAccount || isFounder) {
                                    setIsAddingPrice(true); 
                                    setNewPriceValue(''); 
                                  } else {
                                    setIsProModalOpen(true);
                                  }
                                }}
                                type="button"
                                title={!(isProAccount || isFounder) ? "Adding prices is a Pro feature — click to upgrade" : "Add price"}
                              >
                                <Plus size={13} />
                                Add price
                                {!(isProAccount || isFounder) && <span className="offer-form-pro-badge">PRO</span>}
                              </button>
                            )}
                            {isAddingPrice && (
                              <div className="fm-add-price-inline fm-add-price-inline--below">
                                <span className="fm-price-currency">{getCurrencySymbol(currency)}</span>
                                <input
                                  className="fm-price-input"
                                  type="number"
                                  min="0"
                                  placeholder="0"
                                  value={newPriceValue}
                                  onChange={(e) => setNewPriceValue(e.target.value)}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveNewPrice();
                                    if (e.key === 'Escape') handleCancelAddPrice();
                                  }}
                                />
                                <button className="fm-price-confirm-btn" onClick={handleSaveNewPrice} title="Save">✓</button>
                                <button className="fm-price-cancel-btn" onClick={handleCancelAddPrice} title="Cancel">✕</button>
                              </div>
                            )}
                          </div>
                        )
                      )}

                      {/* Post-TY add-step slot then post-TY steps */}
                      {renderAddStep('after-ty')}
                      {activeOffer.is_custom && activeOffer.funnel_steps &&
                        activeOffer.funnel_steps
                          .slice().sort((a, b) => a.step_order - b.step_order)
                          .filter(step => step.is_post_thank_you)
                          .map(step => (
                            <div key={step.id}>
                              <StepCard
                                id={`custom-step-${step.id}`}
                                title={step.step_name}
                                url={step.url || ""}
                                onDelete={() => deleteStep(step.id, 'custom')}
                                onUrlUpdate={handleUrlUpdate}
                                onVerified={handleStepVerified}
                                isCustomStep={true}
                                stageNumber={getStageNumber('custom', step.id)}
                                offerName={activeOffer.name}
                                price={step.conversion_value}
                                activeOfferId={activeOffer.id}
                                offer={activeOffer}
                                stepId={step.id}
                                stepType={step.is_email_step ? "email" : "custom"}
                                currency={currency}
                                editMode={isEditingOffer}
                              />
                              {renderAddStep(`after-post-ty-${step.id}`)}
                            </div>
                          ))
                      }
                    </>
                  ) : (
                    <div className="shopify-pixel-container">
                      <div className="shopify-pixel-header">
                        <div className="shopify-pixel-header-icon">
                          <img src={shopifyLogo} alt="Shopify" />
                        </div>
                        <div className="shopify-pixel-header-text">
                          <h3>Shopify Pixel Setup</h3>
                          <p>Add this custom pixel to your Shopify store to track clicks and sales</p>
                        </div>
                      </div>
                      <div className="shopify-pixel-body">
                        <div className="shopify-pixel-instructions">
                          <h4>Instructions</h4>
                          <ol>
                            <li>Go to your Shopify admin panel</li>
                            <li>Navigate to <strong>Settings → Customer events → Custom pixels</strong></li>
                            <li>Click <strong>Add custom pixel</strong></li>
                            <li>Give it a name (e.g., "RevTrack Pixel")</li>
                            <li>Copy and paste the code below into the pixel code editor</li>
                            <li>Click <strong>Save</strong></li>
                            <li>Click the three dots, and then select <strong>Connect</strong></li>
                          </ol>
                        </div>
                        <div className="shopify-pixel-code">
                          <pre className="inline-code">{getShopifyPixelCode(activeOffer.name, username)}</pre>
                          <div className="shopify-pixel-code-footer">
                            <button className="copy-code-button" onClick={() => copyShopifyPixelCode(activeOffer.name, username)}>
                              {copiedShopifyPixel ? '✓ Copied!' : 'Copy Pixel Code'}
                            </button>
                          </div>
                        </div>
                        <div className="shopify-pixel-note">
                          <span className="shopify-pixel-note-icon">💡</span>
                          <p><strong>Note:</strong> This pixel will automatically track clicks from your marketing links and sales from your Shopify checkout. No additional code needed on your pages.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Edit mode toggle */}
                {activeOffer.website_editor !== 'shopify' && (
                  <div className="fm-modify-offer-wrap">
                    {isEditingOffer ? (
                      <button
                        className="fm-stop-editing-btn"
                        onClick={() => { setIsEditingOffer(false); setAddStepSlot(null); setAddStepType(null); setEditingPrice(null); }}
                      >
                        <CheckCircle2 size={13} />
                        Done editing
                      </button>
                    ) : (
                      <button
                        className="fm-modify-offer-btn"
                        onClick={() => setIsEditingOffer(true)}
                      >
                        <Pencil size={13} />
                        Modify offer
                      </button>
                    )}
                  </div>
                )}

                {/* Offer complete banner */}
                {(() => {
                  const o = activeOffer;
                  const allVerified = !!(
                    o?.landing_verified_at &&
                    o?.thank_you_verified_at &&
                    (!o?.call_booking_required || o?.call_booking_verified_at) &&
                    (!o?.conversion_value2 || o?.thank_you2_verified_at) &&
                    (!o?.conversion_value3 || o?.thank_you3_verified_at) &&
                    (!o?.funnel_steps?.length || o.funnel_steps.every(s => s.verified_at))
                  );
                  return allVerified ? (
                    <div className="fm-setup-complete-banner">
                      <CheckCircle2 size={16} className="fm-setup-complete-icon" />
                      <span className="fm-setup-complete-text">
                        Offer setup complete! Your last step is to create tracking links.{' '}
                        <Link to="/links" className="fm-setup-complete-link">
                          Go here to finalize your setup
                        </Link>
                      </span>
                    </div>
                  ) : null;
                })()}
              </>
            )}
          </main>
        </div>
        
      )}
      {showConfirmation && (
        <div className="confirm-overlay" onClick={!isDeletingOffer ? () => setShowConfirmation(false) : undefined}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            {isDeletingOffer ? (
              <div className="confirm-loading">
                <Loader2 size={36} className="confirm-spinner" />
                <p className="confirm-loading-text">Deleting offer and all associated data…</p>
              </div>
            ) : (
              <>
                <div className="confirm-icon-wrap">
                  <AlertTriangle size={28} className="confirm-danger-icon" />
                </div>
                <h3 className="confirm-title">Delete Offer Forever?</h3>
                <div className="confirm-body">
                  <p>Make sure to remove this offer from your <strong>landing page</strong> and <strong>thank you page</strong> first — future clicks &amp; conversions <strong>will not be tracked</strong> after deletion.</p>
                  <p>This will permanently <strong>delete all clicks &amp; conversions</strong> associated with this offer. <strong>This cannot be undone.</strong></p>
                </div>
                <div className="confirm-actions">
                  <button className="confirm-cancel-btn" onClick={() => setShowConfirmation(false)}>Cancel</button>
                  <button className="confirm-delete-btn" onClick={handleDeleteOffer}>
                    Delete offer &amp; all data forever
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {showStepDeleteConfirmation && (
        <div className="confirm-overlay" onClick={!isDeletingStep ? () => { setShowStepDeleteConfirmation(false); setStepToDelete(null); } : undefined}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            {isDeletingStep ? (
              <div className="confirm-loading">
                <Loader2 size={36} className="confirm-spinner" />
                <p className="confirm-loading-text">Deleting step and all associated data…</p>
              </div>
            ) : (
              <>
                <div className="confirm-icon-wrap">
                  <AlertTriangle size={28} className="confirm-danger-icon" />
                </div>
                <h3 className="confirm-title">Delete Step?</h3>
                <div className="confirm-body">
                  <p>All <strong>clicks &amp; conversions</strong> tracked for this step will be <strong>permanently deleted</strong>.</p>
                  <p><strong>This cannot be undone.</strong></p>
                </div>
                <div className="confirm-actions">
                  <button
                    className="confirm-cancel-btn"
                    onClick={() => { setShowStepDeleteConfirmation(false); setStepToDelete(null); }}
                  >
                    Cancel
                  </button>
                  <button className="confirm-delete-btn" onClick={confirmDeleteStep}>
                    Delete step &amp; all data
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
};
